// data/strategic-playbook/tectonic-shifts.js
// ════════════════════════════════════════════════════════════════════
// TECTONIC SHIFTS — Strategic Playbook macro-frame model (v3 / F13).
//
// A tectonic shift is a multi-year structural force shaping the
// strategic landscape. v3 splits shifts into three categories:
//   - historical: resolved shifts that serve as the analog library.
//                 Outcomes known. Used to pattern-match active shifts.
//   - active:     shifts being navigated now. The 12 contemporary ones.
//   - emerging:   early signals; may or may not become tectonic.
//
// Stage taxonomy (locked):
//   early        — thesis exists, evidence still fragmentary
//   accelerating — clear directionality, beneficiaries identifiable
//   peak         — late-cycle, returns compressing, mean-reversion risk
//   declining    — structural reversal underway
//   resolved     — historical only; outcome known, lessons extracted
//
// Schema (v3 — extended for paid-tier depth):
//   {
//     id, slug, name, shortName,
//     category: 'historical' | 'active' | 'emerging',
//     era: human-readable date range (e.g. '2020s-' or '1347-1351'),
//     stages, currentStageIndex, stageLabel, note,
//
//     thesis,
//     stageHistory[],
//     beneficiaries[],         // category-level (existing)
//     trappedSectors[],        // category-level (existing)
//     watchFor[], antiWatchFor[],
//     relatedFindings[],
//     crossShiftInteractions[],
//
//     // ─── v3 additions (the depth that earns paid attention) ────
//     contrarianRead: {        // the wedge — what we think vs consensus
//       consensus,             // what the market currently believes
//       lab,                   // what we actually think
//       wedge,                 // why we differ; the asymmetric edge
//     },
//     namedBeneficiaries[],    // specific entities; { entity, ticker?,
//                              //   thesis, risk, sizing }
//     namedTrapped[],          // specific entities to avoid/short
//     watchMetrics[],          // quantitative thresholds with current
//                              //   values; { metric, threshold,
//                              //   currentValue, asOf, interpretation }
//     historicalAnalogs[],     // pattern-matching to past;
//                              //   { shiftId, similarity (0-1),
//                              //     keyDifference }
//     trackRecord[],           // prior calls + outcomes;
//                              //   { date, call, outcome:
//                              //     'held'|'overturned'|'partial'|
//                              //     'pending', notes }
//     lessons[],               // historical only — extracted lessons
//                              //   for analog application
//   }
// ════════════════════════════════════════════════════════════════════

export const TECTONIC_SHIFTS = [
  {
    id: 'ai-boom',
    slug: 'ai-boom',
    name: 'AI Boom',
    shortName: 'AI Boom',
    category: 'active',
    era: '2022-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 1,
    stageLabel: 'Accelerating',
    note: 'Year 4. Picks-and-shovels exhausted. Vertical AI underexplored.',

    thesis:
      "The AI Boom is transitioning from infrastructure-build (foundation models, GPU buildout, hyperscaler capex) into vertical-application + agent-economy phase. Capital allocation has rotated: the next 3-5 years are not about who trains the biggest model but about which vertical AI applications produce 10× productivity gains AND can be defended against incumbents. The 'picks-and-shovels' trade is largely priced; the next leg is application-layer differentiation, agent-economy infrastructure, and the regulatory-arbitrage geography that hosts compute.",

    stageHistory: [
      { date: '2022-Q4', stage: 'early', note: 'ChatGPT public release. Thesis crystallizes for generalist users.' },
      { date: '2023',    stage: 'early', note: 'Foundation-model wars (GPT-4, Claude, Gemini). Picks-and-shovels capex commences.' },
      { date: '2024',    stage: 'early → accelerating', note: 'Enterprise adoption begins clearing ROI thresholds. Hyperscaler capex spike.' },
      { date: '2025',    stage: 'accelerating', note: 'Vertical-AI emergence. Agent-economy thesis crystallizes. First $1B+ vertical-AI exits.' },
    ],

    beneficiaries: [
      'Vertical AI with proprietary data moats (medical, legal, financial-services specialty)',
      'AI compute / inference infrastructure (chips, networking, datacenter cooling)',
      'Agent-economy enablers (browser-automation tools, computer-use APIs, identity-and-payment rails)',
      'Regulatory-arbitrage geographies hosting frontier compute',
      'Application-layer companies with distribution-channel lock-in pre-AI',
    ],

    trappedSectors: [
      'Pure foundation-model labs without a vertical wedge or distribution moat',
      'Legacy SaaS with feature-by-feature competition against AI-native entrants',
      'Consultancies billed on engineer-hours rather than outcomes',
      'Junior-coder-heavy IT services with no systems-architecture differentiator',
      'Content-mill businesses (SEO / spec-content) facing zero-marginal-cost competitors',
    ],

    watchFor: [
      'Agent-economy traction — measurable revenue from autonomous workflows, not demos',
      'Enterprise AI ROI clearing 3× sustained for non-pilot deployments',
      'Vertical-AI exits at $1B+ valuations with revenue-attached, not growth-attached, multiples',
      'AI-native startups crossing $100M ARR in <3 years from launch',
      'Foundation-model price compression to commodity tier with sustained demand still bid',
    ],

    antiWatchFor: [
      'Foundation-model commoditization that destroys training-economics moats faster than vertical wedges form',
      'Sustained energy or compute constraints capping deployment scale',
      'AI regulation pushing deployment compliance cost above $10M / quarter for mid-cap operators',
      'Agent-economy stalls: trust / liability gaps preventing autonomous-action adoption',
    ],

    relatedFindings: [], // open slot — Lab finding on AI-Boom dynamics not yet authored

    crossShiftInteractions: [
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'AI labor substitution accelerates as working-age populations contract; the demographic pressure is the demand-side floor under productivity-AI applications.' },
      { otherShiftId: 'us-china-decoupling', direction: 'compounding',
        mechanism: 'Bifurcated AI ecosystems form along chip-control + training-data nationalization lines; AI Boom outcomes become path-dependent on which ecosystem an operator is in.' },
      { otherShiftId: 'energy-transition', direction: 'compounding',
        mechanism: 'Hyperscaler power demand from AI training/inference is now a primary driver of energy-transition urgency — particularly nuclear renaissance and grid investment.' },
      { otherShiftId: 'humanoid-robotics', direction: 'compounding',
        mechanism: 'Humanoid robotics is the embodied half of the AI Boom. Without it, AI is software-only; with it, AI substitutes for physical labor across the economy.' },
      { otherShiftId: 'asset-tokenization', direction: 'compounding',
        mechanism: 'Programmable money (stablecoins / tokenized rails) is the financial substrate AI agents need to transact. Tokenization is the AI agent-economy enabler.' },
    ],
  },

  {
    id: 'energy-transition',
    slug: 'energy-transition',
    name: 'Energy Transition',
    shortName: 'Energy Transition',
    category: 'active',
    era: '2010s-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: 'Nuclear renaissance + grid + storage. Solar already priced in.',

    thesis:
      "The 'energy transition' framing understates the actual structural shift: solar PV is largely priced in and is no longer the frontier. The frontier is grid-scale storage, the nuclear renaissance (small modular reactors + life-extension of existing fleet), industrial-scale electrification (datacenters, electrified heat, EV truck charging), and the multi-decade transmission rebuild against a deteriorating-baseload political economy. AI-driven power demand has clarified the constraint: it is grid-and-baseload, not generation-cost. The transition is a multi-decade infrastructure rebuild, not a renewables story.",

    stageHistory: [
      { date: '2010-2020', stage: 'pre-transition', note: 'Solar/wind cost curves cross thresholds. Renewables become economic at scale.' },
      { date: '2020-2023', stage: 'early', note: 'Grid + storage realization phase begins. First major nuclear-life-extension agreements signed.' },
      { date: '2023',      stage: 'early', note: 'Nuclear narrative shifts. SMR licensing accelerates. First major US plant approvals since 1970s.' },
      { date: '2024-2025', stage: 'early', note: 'AI-driven power demand spike clarifies grid constraint. Hyperscaler nuclear PPAs (Microsoft+Constellation/3MI, Amazon+X-Energy) signal new buyer class.' },
    ],

    beneficiaries: [
      'Nuclear (SMR, fuel cycle, life-extension, advanced reactors)',
      'Grid + transmission infrastructure',
      'Long-duration storage (4-100hr) — lithium, vanadium-flow, iron-air, thermal',
      'Uranium fuel cycle (mining, enrichment, fabrication)',
      'Specific utilities with reactive nuclear or grid-scale exposure',
      'Copper, electrical-grid materials, transformer manufacturing',
      'Industrial heat-pump and electrification specialists',
    ],

    trappedSectors: [
      'Solar-panel manufacturers without distribution / installation moats',
      'Utility-scale solar developers in oversupplied markets (Texas, California behind-meter)',
      'Pure EV plays without battery / charging / energy-management integration',
      'Coal-dependent utilities without clear transition path',
      'Natural-gas peaker plants in markets where storage is approaching cost-parity',
    ],

    watchFor: [
      'SMR licensing breakthroughs (NuScale, X-Energy, Holtec, Westinghouse AP300)',
      'First commercial SMR coming online — likely 2028-2030 window',
      'Utility nuclear PPAs at hyperscaler scale (already happening; watch for second wave)',
      'Transmission-line approval acceleration (FERC reforms, state-level)',
      'Storage cost-curve breaks below $100/kWh installed for 4-hour systems',
    ],

    antiWatchFor: [
      'Nuclear regulatory reversal (post-incident or political shift)',
      'Sustained natural-gas cost compression (<$2.50/MMBtu) eroding electrification economics',
      'Storage cost decline below $50/kWh that obviates baseload anxieties before nuclear can scale',
      'Federal-level rollback of IRA / industrial-policy framework',
    ],

    relatedFindings: [], // open slot

    crossShiftInteractions: [
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'AI compute-power demand is now a primary driver of energy-transition urgency. The energy investment thesis is partly an AI-infrastructure thesis.' },
      { otherShiftId: 'us-china-decoupling', direction: 'conflicting',
        mechanism: 'Critical-mineral supply chains (lithium, nickel, cobalt, rare earths, gallium for grid) are concentrated in China-aligned geographies; decoupling raises transition-component costs and slows deployment.' },
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'Aging engineering workforce + shrinking trades pipeline = workforce constraint compounding the capital constraint on transition buildout.' },
      { otherShiftId: 'climate-adaptation', direction: 'compounding',
        mechanism: 'Adaptation and mitigation share infrastructure backbone (grid hardening, distributed energy). Transition investment that ignores adaptation builds infrastructure that can\'t survive the warming already locked in.' },
    ],
  },

  {
    id: 'us-china-decoupling',
    slug: 'us-china-decoupling',
    name: 'US/China Decoupling',
    shortName: 'US/China Decoupling',
    category: 'active',
    era: '2018-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 1,
    stageLabel: 'Accelerating',
    note: 'Reshoring beneficiaries underpriced. Watch chip supply.',

    thesis:
      "Bipartisan US consensus has crystallized around strategic decoupling in semiconductors, advanced manufacturing, AI, biotech, and critical minerals. This is structural, not cyclical, and survives administration changes. The reshoring dividend goes to specific corridors (Mexico, Vietnam, India, Eastern Europe) and specific technology stacks; pure China-export plays face a structural headwind regardless of valuation. The pace of decoupling is set by industrial-policy lag and supply-chain inertia, not by political appetite — so the trajectory is largely locked in for 5-10 years.",

    stageHistory: [
      { date: '2018',      stage: 'early', note: 'Trump administration tariffs initiate Phase 1 trade war. Decoupling thesis crystallizes.' },
      { date: '2019-2022', stage: 'early', note: 'Tech restrictions expand: Huawei, ZTE, semiconductor export controls, entity list growth.' },
      { date: '2022',      stage: 'early → accelerating', note: 'CHIPS Act passes. Industrial policy era begins. BIS Oct-2022 export controls reshape semi industry.' },
      { date: '2023-2024', stage: 'accelerating', note: 'BIS expanded controls (multilateral). Outbound-investment screening framework. Mexico-Vietnam reshoring beneficiaries clarify.' },
      { date: '2025',      stage: 'accelerating', note: 'Bipartisan consensus stable across 2024 election. Indian PLI scheme uptake accelerating. Critical-minerals processing buildout.' },
    ],

    beneficiaries: [
      'Mexico / Vietnam / India contract-manufacturing capacity',
      'US-domiciled fabs, EUV-tool ecosystem (ASML, ASMI, Lam, AMAT), advanced packaging',
      'Critical-minerals processing — rare earths, gallium, germanium, lithium-refining outside China',
      'Eastern European auto-parts and industrial-component manufacturers',
      'Australian critical-minerals miners with Western-aligned offtake',
      'India-domiciled tech-services with US client books and Indian engineering pipeline',
      'Defense-industrial-base specialists with government-procurement leverage',
    ],

    trappedSectors: [
      'China-domiciled semiconductor design and IP-licensing',
      'Apple-supplier ecosystem with single-China assembly exposure',
      'Taiwan-strait-risk-concentrated supply chains without Diversification path',
      'Multinationals with >40% China revenue exposure and no India / Vietnam / Mexico pivot in motion',
      'Dual-listed Chinese ADRs facing delisting and capital-flight pressure',
    ],

    watchFor: [
      'Mexico FDI inflow vs Chinese outflow (the leading reshoring indicator)',
      'Semiconductor capacity decisions — TSMC Arizona ramp, Intel Ohio, Samsung Texas',
      'Indian PLI-scheme uptake by category (electronics, pharma, semis)',
      'Expanding outbound-investment restrictions (semis → biotech → AI → ?)',
      'Taiwan-strait incidents with even minor escalatory signaling',
      'Critical-minerals-processing announcements outside China',
    ],

    antiWatchFor: [
      'G2 thaw scenario (low probability but watch back-channel signals)',
      'China achieving 5nm-equivalent domestic capacity at production scale',
      'US-EU trade dispute pulling decoupling resources away from China focus',
      'Biotech/pharma carve-outs that signal decoupling appetite is narrower than assumed',
    ],

    relatedFindings: [], // open slot — strategic finding on decoupling beneficiaries possible

    crossShiftInteractions: [
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'Bifurcated AI ecosystems form along chip-control lines. AI Boom outcomes are path-dependent on which ecosystem an operator participates in.' },
      { otherShiftId: 'energy-transition', direction: 'conflicting',
        mechanism: 'Critical-minerals competition; nuclear fuel-cycle exposure (Russia/Kazakhstan vs Western alternatives). Decoupling raises transition-component costs.' },
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'China\'s demographic crunch is itself a strategic-decoupling factor — accelerated industrial-policy aggression as the working-age window closes.' },
      { otherShiftId: 'defense-industrial-base', direction: 'compounding',
        mechanism: 'Defense-industrial decoupling is a primary lever in the broader US/China decoupling. The two shifts share supply-chain constraints and procurement-reform tailwinds.' },
      { otherShiftId: 'space-economy', direction: 'compounding',
        mechanism: 'Bifurcated space ecosystems (Chinese space station, lunar competition, navigation-system competition between GPS and BeiDou). Space is now a strategic-trade domain.' },
    ],
  },

  {
    id: 'demographics',
    slug: 'demographics',
    name: 'Population Decline (Developed)',
    shortName: 'Population Decline',
    category: 'active',
    era: '1990s-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early (in awareness)',
    note: 'Japan was preview. Korea, Italy, China next. 30-year arc, structural-cultural substrate is the lever.',

    thesis:
      "Sub-replacement fertility is now structural across OECD and increasingly across middle-income economies. The Lab corpus operationalizes the load-bearing claim: pronatalist spending compensates at margins, but cannot create the cultural-structural conditions (housing, working hours, gender norms, neighborhood child-density, intergenerational caregiving substrate) that lift fertility durably. Necessary-but-insufficient. Korea is preview; Italy + Japan + Eastern Europe + China are mid-arc; Germany, France, Sweden have begun the trajectory. The 30-year window is largely locked in — investment thesis is adaptation infrastructure, not reversal.",

    stageHistory: [
      { date: '1990-2000', stage: 'pre-decline', note: 'Sub-replacement fertility stabilizes across OECD. Japan ahead of cohort.' },
      { date: '2003',      stage: 'early', note: 'Korea crosses below 1.5 (lowest-low fertility threshold).' },
      { date: '2008-2012', stage: 'early', note: 'Hungary launches major pronatalist program. Tests the policy thesis at 6.2% GDP intensity.' },
      { date: '2018-2024', stage: 'early', note: 'Korea reaches 0.72 — lowest fertility recorded anywhere, ever. Hungary peaks at 1.59 then reverses.' },
      { date: '2026-05',   stage: 'early', note: 'Lab pre-registers OECD-wide falsification test of policy-cannot-create thesis. Resolution 2030-2035.' },
      { date: '2030-2035', stage: 'early → accelerating (projected)', note: 'Cohort-completion data resolves the policy thesis. Adaptation-infrastructure investment thesis crystallizes.' },
    ],

    beneficiaries: [
      'Old-age services — medical, legal, financial planning, geriatric specialty',
      'Robotics for caregiving and labor substitution in care contexts',
      'Immigration-friendly economies with absorption capacity (Australia, Canada, parts of US)',
      'Productivity-enhancing technology with declining-workforce thesis (industrial automation, AI, agentic workflows)',
      'Senior-housing real estate, assisted-living infrastructure',
      'Pension-restructuring advisory, longevity-insurance products',
      'Healthcare logistics for aged populations (home-health, telemedicine, remote monitoring)',
    ],

    trappedSectors: [
      'Demographics-dependent consumer markets in lowest-low countries (Korea, Italy, Japan)',
      'Legacy pension systems without reform path (most of Western Europe, Japan)',
      'Education systems sized for prior cohorts — universities, K-12 infrastructure in declining regions',
      'Consumer brands targeting young families in lowest-low markets',
      'Real-estate exposure to outlying / non-immigration-corridor regions in declining countries',
    ],

    watchFor: [
      'Cohort fertility data for women born 1990-95 as it arrives 2030+ (the Lab falsification test resolves on this)',
      'Immigration-policy shifts in lowest-low countries (Japan, Korea, Italy)',
      'Pension-system structural reform attempts (raise retirement age, adjust replacement rate)',
      'First commercial AI-caregiving deployments at scale',
      'Cultural-structural shift indicators — housing affordability, working-hours policy, gender hours-gap reduction',
      'Eastern European demographic outflow accelerating',
    ],

    antiWatchFor: [
      'One of the 8 Lab watch-list countries surprising with cohort recovery (would falsify the thesis)',
      'Large-scale immigration shifts in Japan or Korea (would buy time, not solve)',
      'Cultural-structural shifts (housing, hours, gender norms) showing measurable change in any lowest-low country',
      'Major religious-revival / community-restructuring patterns that produce Israel-style sustained recovery',
    ],

    relatedFindings: [
      'lab:finding/popdec/2026/korea-hungary-divergence/v1',
      'lab:finding/popdec/2026/korea-lockin/v1',
      'lab:finding/popdec/2026/positive-case-search/v1',
    ],

    crossShiftInteractions: [
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'AI labor substitution becomes existential, not optional. The demographic pressure is the demand-side floor under productivity-AI.' },
      { otherShiftId: 'energy-transition', direction: 'compounding',
        mechanism: 'Aging engineering workforce + shrinking trades pipeline = the workforce constraint compounding capital constraints on transition buildout.' },
      { otherShiftId: 'us-china-decoupling', direction: 'compounding',
        mechanism: "China's demographic crunch is a strategic-decoupling factor — accelerated industrial-policy aggression as the working-age window closes." },
      { otherShiftId: 'longevity-biology', direction: 'compounding',
        mechanism: 'Longevity intervention extends working-age window, partially compensating for fertility decline. The two shifts are economic substitutes at the population-pyramid level.' },
      { otherShiftId: 'humanoid-robotics', direction: 'compounding',
        mechanism: 'Humanoid robotics in care contexts is one of the few credible adaptation paths to working-age population contraction. The demographic pressure is the demand floor under care-economy automation.' },
    ],
  },

  // ── NEW IN F12.1 ────────────────────────────────────────────────

  {
    id: 'humanoid-robotics',
    slug: 'humanoid-robotics',
    name: 'Humanoid Robotics',
    shortName: 'Humanoid Robotics',
    category: 'active',
    era: '2023-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: 'Year 1-2. Tesla Optimus, Figure, Agility, Unitree. Hardware-software flywheel just starting. Expensive, fragile, scaling fast.',

    thesis:
      "The convergence of vision-language-action AI, low-cost actuators (electric drives + China supply chain), and battery improvements has made humanoid robotics economically plausible for the first time. The 2020s buildout phase mirrors EVs in 2010 — early prototypes that look fragile and expensive but with a clear cost-curve trajectory. Medium-term thesis: labor substitution in structured environments (warehousing, manufacturing, light assembly) by 2028; broader-environment deployment (eldercare, retail, hospitality) by 2032. The shift is the embodied half of the AI Boom — without humanoids, AI is software-only; with them, AI substitutes for physical labor across the economy.",

    stageHistory: [
      { date: '2010-2020', stage: 'pre-shift', note: 'ASIMO-era humanoids — slow, expensive, no commercial path. AI not yet capable enough to drive them.' },
      { date: '2021-2022', stage: 'early', note: 'Tesla Bot announced; Optimus prototype shown. Vision-language-action models become serious.' },
      { date: '2023',      stage: 'early', note: 'Figure, 1X, Agility, Sanctuary, Unitree all advance prototypes. Pilots begin (BMW, Mercedes-Benz).' },
      { date: '2024',      stage: 'early', note: 'First commercial pilots at scale (Amazon Digit, Mercedes Apptronik). China supply chain mass production starts.' },
      { date: '2025',      stage: 'early', note: 'Cost compression beginning. Sub-$30K humanoid in roadmap. Real-world data flywheel forming.' },
    ],

    beneficiaries: [
      'Humanoid manufacturers with vertical-integration moats (Tesla, Figure, Agility, Boston Dynamics)',
      'Actuator / motor specialists with humanoid-specific designs',
      'Power-density battery and thermal-management specialists',
      'Vision-language-action AI labs with on-device inference roadmap',
      'Industrial-deployment integration services (the SI layer for humanoid retraining)',
      'Real-world data flywheel companies focused on robotics',
      'China humanoid supply chain (Unitree, Xiaomi, UBTech, Fourier)',
    ],

    trappedSectors: [
      'Industrial-automation incumbents tied to fixed-installation robots without humanoid roadmap',
      'Labor-arbitrage businesses in commodity-task environments (warehouse, light assembly)',
      'Staffing / temp-labor businesses in automatable contexts',
      'Single-skill industrial robotics with no multi-task pivot',
    ],

    watchFor: [
      'First sub-$30K humanoid commercially available (consumer-or-light-industrial)',
      'Sustained 1000+ hour MTBF (mean time between failures) in real deployments',
      '24/7 deployment in warehouse / manufacturing — not pilot, real production',
      'China-supply-chain humanoids hitting global markets at $10-15K BOM',
      'Insurance / liability frameworks emerging for autonomous physical systems',
      'First $1B+ humanoid-only revenue quarter from any single operator',
    ],

    antiWatchFor: [
      'Sustained "uncanny valley" social rejection in service / consumer contexts',
      'Battery / actuator cost curves stalling above $50K BOM',
      'AI-control unreliability blocking out-of-cell deployment',
      'Labor-protection regulation slowing deployment in major economies (EU likely first)',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'Humanoid robotics is the embodied manifestation of agentic AI. Without humanoids, AI is software-only; with them, AI substitutes for physical labor.' },
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'Care-economy robotics is one of the few credible adaptation paths to working-age population contraction.' },
      { otherShiftId: 'us-china-decoupling', direction: 'compounding',
        mechanism: 'Bifurcation between Tesla/Figure/Boston Dynamics (US) and Unitree/Xiaomi/UBTech (China) at hardware level. Humanoid hardware becomes a strategic-trade category.' },
    ],
  },

  {
    id: 'space-economy',
    slug: 'space-economy',
    name: 'Space Economy',
    shortName: 'Space Economy',
    category: 'active',
    era: '2015-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 1,
    stageLabel: 'Accelerating',
    note: 'Falcon-9 reusability rewrote unit economics 10×. Starship + satellite economy + lunar return. SpaceX dominant; competition emerging.',

    thesis:
      "SpaceX's Falcon-9 reusability collapsed cost-per-kg-to-orbit by ~10×, opening a multi-decade economic frontier — satellite communications (Starlink, Kuiper), Earth observation, in-space manufacturing, lunar logistics, and eventually planetary commerce. The 2010s was the cost-curve break; the 2020s is the application-layer buildout. Regulatory bottleneck (FAA), supply chain (turbomachinery, propellants, on-orbit servicing), and orbital-debris risk are the binding constraints, not capital. The shift is structural and durable for at least another decade.",

    stageHistory: [
      { date: '2010-2015', stage: 'pre-shift', note: 'SpaceX validates booster recovery and reuse on suborbital tests.' },
      { date: '2017-2018', stage: 'early', note: 'Falcon Heavy demonstrated. Cost-per-kg drops below traditional providers; competition pricing crisis begins.' },
      { date: '2020-2022', stage: 'early → accelerating', note: 'Starlink commercial rollout. Satellite-economy thesis crystallizes; constellation competition (Kuiper, OneWeb) enters.' },
      { date: '2023-2024', stage: 'accelerating', note: 'Starship test campaign. Orbital refueling architecture emerges. Hyperscaler interest in space-resident compute.' },
      { date: '2025',      stage: 'accelerating', note: 'Lunar return programs (Artemis), commercial space stations in design (Axiom, Starlab, Orbital Reef). DoD/NRO embracing commercial-launch + commercial-imagery.' },
    ],

    beneficiaries: [
      'SpaceX-adjacent supply chain (turbomachinery, advanced manufacturing, propellants, composites)',
      'Satellite-services beyond comms (Earth observation daily-revisit, IoT, RF-spectrum monitoring)',
      'Ground-station infrastructure operators (KSAT, Viasat, Amazon AWS Ground)',
      'Defense-contractor space programs (with new-entrant cost pressure forcing modernization)',
      'In-space manufacturing / pharma R&D (microgravity-only products)',
      'Specialty chip and radiation-hardened electronics suppliers',
      'Commercial-launch alternatives building reusable architectures (Stoke, Rocket Lab Neutron, Relativity)',
    ],

    trappedSectors: [
      'Legacy launch providers without reusability roadmap (ULA, Arianespace, Roscosmos)',
      'Satellite operators on legacy GEO architecture without LEO migration path',
      'Geospatial-imagery monopolists threatened by daily-revisit constellation competition',
      'Government-cost-plus-only contractors facing commercial-procurement reform',
    ],

    watchFor: [
      'Starship orbital refueling demonstration → unlocks deep-space economics',
      'Commercial space-station operations begin (Axiom, Starlab, Orbital Reef)',
      'Satellite-internet ARPU clearing $50/mo at scale (commercial profitability proof)',
      'Reusable second-stage flight cadence (Stoke, Starship full reuse)',
      'Lunar surface logistics contract awards (NASA + commercial)',
      'In-space manufacturing first commercial product (Varda, others)',
    ],

    antiWatchFor: [
      'Starship sustained launch failure triggering FAA / regulatory shutdown window',
      'LEO debris cascade (Kessler-syndrome-light) increasing insurance / orbital-slot costs',
      'Major regulatory crackdown on satellite-internet spectrum',
      'Sustained commodity-launch pricing power erosion (more entrants commoditize before vertical applications mature)',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'AI-accelerated satellite-data interpretation and space autonomy. AI Boom + Space → automated Earth-observation analytics at scale.' },
      { otherShiftId: 'defense-industrial-base', direction: 'compounding',
        mechanism: 'Space-domain awareness, anti-satellite capability, persistent surveillance. Defense rebuild has space as a primary domain.' },
      { otherShiftId: 'us-china-decoupling', direction: 'compounding',
        mechanism: 'Bifurcated space ecosystems (Chinese space station, lunar competition, navigation-system competition between GPS and BeiDou).' },
    ],
  },

  {
    id: 'crispr-gene-editing',
    slug: 'crispr-gene-editing',
    name: 'CRISPR / Gene Editing',
    shortName: 'CRISPR',
    category: 'active',
    era: '2012-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: 'Casgevy approval (Dec 2023) was the threshold event. Research tool → approved therapeutic. Sickle cell first; oncology + ag pipeline next.',

    thesis:
      "CRISPR-Cas9 (and now base editors, prime editors, Cas12, Cas13) crossed the research-to-therapeutic threshold with FDA approval of Casgevy (Vertex / CRISPR Therapeutics) for sickle cell disease in December 2023. The 2020s is the platform-extension phase: in vivo delivery solves, oncology applications mature, crop-genomics scales (especially outside US/EU), and reimbursement frameworks for $2-3M one-time cures stabilize. The structural shift is gene editing becoming another tool in the medicine toolbox, not a single-disease bet — analogous to monoclonal antibodies' transition from novelty to standard modality in the 2000s.",

    stageHistory: [
      { date: '2012',     stage: 'pre-shift', note: 'Charpentier-Doudna paper. CRISPR-Cas9 demonstrated as gene-editing tool.' },
      { date: '2017-2020', stage: 'early', note: 'First clinical trials. Cas9 patent disputes (Broad vs UC).' },
      { date: '2020',     stage: 'early', note: 'Charpentier + Doudna Nobel Prize; mainstreaming.' },
      { date: '2023-Dec', stage: 'early', note: 'FDA approves Casgevy for sickle cell — first CRISPR therapeutic anywhere.' },
      { date: '2024-2025', stage: 'early', note: 'Pipeline expansion (oncology, genetic blindness, hypercholesterolemia trials). LNP/AAV delivery scaling.' },
    ],

    beneficiaries: [
      'CRISPR-platform companies (Vertex, CRISPR Therapeutics, Intellia, Beam, Verve, Editas)',
      'Lipid-nanoparticle (LNP) delivery specialists',
      'AAV manufacturing capacity (CDMOs)',
      'Ag-biotech with CRISPR-edited crops (Pairwise, Yield10, Cibus)',
      'Specialty-pharma manufacturing for gene therapies',
      'Genetic-screening / diagnostics partners',
      'Oncology platforms integrating CRISPR with CAR-T / cell therapies',
    ],

    trappedSectors: [
      'Generic-drug companies in genetic-disease therapeutic categories',
      'Symptomatic-treatment franchises in newly-curable diseases',
      'Animal-husbandry / industrial-ag without genetics platform exposure',
      'Long-tail rare-disease drug developers (now competing with one-time cures)',
    ],

    watchFor: [
      'In vivo CRISPR delivery solving (currently mostly ex vivo — limits applications)',
      'First $100M+ Casgevy quarter — commercialization velocity',
      'Verve Therapeutics PCSK9 trial readout (would unlock cardiology)',
      'Oncology CAR-T integration with CRISPR editing',
      'Crop-genomics regulatory shifts in EU',
      'Insurance/reimbursement frameworks for $2-3M one-time cures',
    ],

    antiWatchFor: [
      'Off-target editing-related safety event in major trial',
      'Patent litigation slowing US deployment',
      'Reimbursement structures making $2-3M cures inaccessible',
      'Public-health policy reaction limiting germline-editing pathways',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'longevity-biology', direction: 'compounding',
        mechanism: 'CRISPR-based reprogramming, senolytic targeting, and telomere editing are core mechanisms in the longevity stack.' },
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'Gene editing is part of the adaptation infrastructure for aging populations — curing genetic disease at the source rather than managing it lifelong.' },
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'AI-driven protein design (AlphaFold lineage) accelerates target identification and guide-RNA optimization.' },
    ],
  },

  {
    id: 'longevity-biology',
    slug: 'longevity-biology',
    name: 'Longevity Biology',
    shortName: 'Longevity',
    category: 'active',
    era: '2021-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: 'GLP-1s opened the door — metabolic health as treatable. Rapamycin, senolytics, regenerative medicine on deck. 5-10y arc to "aging as treatable."',

    thesis:
      "GLP-1 receptor agonists (Ozempic, Mounjaro, Wegovy) opened the public and regulatory imagination to 'metabolic health as a treatable disease state' — the gateway thesis for longevity biology. The 2020s is the validation phase: rapamycin trials, senolytic clinical readouts, NAD+ pathway modulators, and the broader translation of decades of academic geroscience research into approved therapeutics. Structural enablers: AI-driven biomarker discovery, GLP-1 commercial validation making investor capital flow, demographic urgency (population decline thesis). The shift is the slow extension of healthspan from biological inevitability to clinical target.",

    stageHistory: [
      { date: '1990s-2010s', stage: 'pre-shift', note: 'Geroscience academic research (mTOR, sirtuins, autophagy, senescence). Mostly niche.' },
      { date: '2017-2020',  stage: 'pre-shift', note: 'First rapamycin trials in dogs (Dog Aging Project). Pets-to-humans translation pathway emerges.' },
      { date: '2021',       stage: 'early', note: 'Ozempic / Wegovy approvals. Metabolic health enters mainstream awareness.' },
      { date: '2023-2024',  stage: 'early', note: 'Rapamycin off-label use at scale. Senolytic trials advance. Altos Labs / NewLimit / Retro Bio funded at multi-billion levels.' },
      { date: '2025',       stage: 'early', note: 'First major senolytic Phase 3 readouts; rapamycin formal aging trials begin.' },
    ],

    beneficiaries: [
      'GLP-1 manufacturers and supply chain (Novo Nordisk, Eli Lilly, peptide CDMOs)',
      'Senolytic / rejuvenation biotech (UNITY, Rubedo, Loyal — pet-division-of-human-market structure)',
      'Diagnostic companies for biological-age / multi-omics testing',
      'Wellness retail with longevity-clinic integration',
      'Specialty-pharmacy compounding (peptides, off-label)',
      'Continuous-glucose-monitoring + metabolic-health platforms',
      'Reprogramming-platform biotech (Altos, NewLimit, Retro Biosciences)',
    ],

    trappedSectors: [
      'Treatment-of-disease franchises in metabolic, cardiovascular, oncology where prevention competes',
      'Sugar / processed-food industry (already pivoting; long arc)',
      'Junior-pharma in symptomatic-treatment categories',
      'Health-insurance models that don\'t price prevention',
    ],

    watchFor: [
      'First Phase 3 senolytic readout in humans',
      'Rapamycin formal aging-indication approval (or major academic trial outcome)',
      'GLP-1 cost compression below $100/month at scale',
      'Multi-omics aging biomarkers reaching clinical adoption',
      'AI-driven longevity drug discovery first major hit',
      'Insurance reimbursement for biological-age testing',
    ],

    antiWatchFor: [
      'Major safety event in chronic-use rapamycin or GLP-1 cohorts',
      'Public-health backlash to "anti-aging" framing',
      'Regulatory pushback on aging-as-indication trials',
      'Capital-flight from preclinical longevity startups (Altos cool-down)',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'Longevity intervention extends working-age window, partially compensating for fertility decline. Economic substitutes at the population-pyramid level.' },
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'AI-driven aging biomarker discovery and drug design accelerate the validation phase.' },
      { otherShiftId: 'crispr-gene-editing', direction: 'compounding',
        mechanism: 'Epigenetic reprogramming and telomere management are CRISPR-stack interventions; the two shifts converge in regenerative-medicine applications.' },
    ],
  },

  {
    id: 'defense-industrial-base',
    slug: 'defense-industrial-base',
    name: 'Defense Industrial Base Rebuild',
    shortName: 'Defense Industrial Base',
    category: 'active',
    era: '2022-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 1,
    stageLabel: 'Accelerating',
    note: 'Russia/Ukraine + Israel + Taiwan strait + Houthis catalyzed multi-year buildout. Munitions production capacity is the bottleneck.',

    thesis:
      "After three decades of post-Cold-War atrophy, the US and allied defense industrial base is undergoing a structural rebuild. The convergence of Russia's invasion of Ukraine (revealing munitions-stockpile depletion), Israel's multi-front war (revealing inventory pressure), Houthi Red Sea disruption, and Taiwan-strait risk pricing have catalyzed multi-year contracts and capacity buildout. This is not a cyclical defense cycle — it's a structural reconstitution of capacity that takes 8-15 years to build (specialty steel, primer powders, motors, microelectronics for weapons systems). New entrants with dual-use technology + Pentagon procurement-reform tailwinds are reshaping the landscape.",

    stageHistory: [
      { date: '1990-2015', stage: 'pre-shift', note: 'Post-Cold-War defense-industrial atrophy. Consolidation into 5 primes. Munitions inventory drawn down.' },
      { date: '2016-2021', stage: 'pre-shift', note: 'National Defense Strategy refocuses on great-power competition; Pacific reorientation begins.' },
      { date: '2022 (Feb)', stage: 'early → accelerating', note: 'Russia invades Ukraine. Stinger / Javelin / 155mm depletion exposed publicly.' },
      { date: '2023-2024', stage: 'accelerating', note: 'Israel multi-front war, Red Sea disruption. New-entrant momentum (Anduril, Shield AI, Saronic, Skydio).' },
      { date: '2025',     stage: 'accelerating', note: 'Multi-year contracts begin. Specialty munitions capacity buildout. AUKUS submarine production.' },
    ],

    beneficiaries: [
      'Specialty munitions manufacturers (artillery shells, missile motors, primers, explosives)',
      'Dual-use / new-entrant defense tech (Anduril, Shield AI, Saronic, Skydio)',
      'Microelectronics for defense (rad-hard, RF, SiGe specialty)',
      'Specialty-steel / strategic-materials suppliers',
      'AUKUS submarine industrial base (Electric Boat, BAE, Rolls-Royce nuclear)',
      'Drone manufacturing onshored (counter-China supply chain)',
      'Maintenance / depot capacity for legacy fleet',
    ],

    trappedSectors: [
      'Pure-commercial aerospace exposed to defense-displacement at primes',
      'Companies dependent on China defense-component supply chains',
      'Legacy primes without dual-use / autonomy roadmap',
    ],

    watchFor: [
      '155mm artillery production rate clearing 100K shells/month',
      'New-entrant defense tech IPO velocity (Anduril class)',
      'AUKUS submarine production milestones',
      'Pentagon procurement reform implementation',
      'Specialty-materials investment (e.g., rare-earth processing onshored)',
      'Munitions multi-year contract authority expansion',
    ],

    antiWatchFor: [
      'Federal-budget political fragmentation slowing multi-year contracts',
      'Russia-Ukraine ceasefire reducing political urgency',
      'Major safety / quality issue in new-entrant procurement',
      'China supply-chain crisis disrupting Western component flow',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'us-china-decoupling', direction: 'compounding',
        mechanism: 'Defense-industrial decoupling is a primary lever in the broader US/China decoupling. The two shifts share supply-chain constraints.' },
      { otherShiftId: 'space-economy', direction: 'compounding',
        mechanism: 'Space-domain capability, persistent surveillance, and launch as defense are integral to the rebuild.' },
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'Autonomous weapons + decision-support AI integration. Defense procurement is one of the largest near-term applications of AI Boom investment.' },
    ],
  },

  {
    id: 'climate-adaptation',
    slug: 'climate-adaptation',
    name: 'Climate Adaptation Economy',
    shortName: 'Climate Adaptation',
    category: 'active',
    era: '2020s-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: '2°C is locked. Adaptation infrastructure (water, ag, insurance, hardening) becomes its own thesis distinct from mitigation.',

    thesis:
      "As 2°C of warming becomes locked in (regardless of mitigation pace), adaptation infrastructure shifts from a fringe concern to a primary investment thesis distinct from energy transition (which is mitigation). Categories: water management (desalination, distributed treatment, agricultural efficiency), agricultural adaptation (heat/drought-tolerant crops, indoor agriculture, alternative protein), insurance restructuring (parametric, retreat from uninsurable geographies), and infrastructure hardening (transmission, transportation, coastal). Demographic-and-migration second-order effects compound the investment case. The shift is the recognition that mitigation alone is no longer sufficient — both must run.",

    stageHistory: [
      { date: '1990-2010', stage: 'pre-shift', note: 'Climate framed almost exclusively as future risk; mitigation-only paradigm.' },
      { date: '2010-2020', stage: 'pre-shift', note: 'First major insurance-market signals (Florida property insurance withdrawals, California wildfire).' },
      { date: '2020-2023', stage: 'early', note: 'ESG → adaptation focus shift; physical-risk pricing starts to enter real-estate, insurance, infrastructure investment.' },
      { date: '2023-2024', stage: 'early', note: 'Water-rights crisis (Colorado River), Phoenix heat domes, parametric-insurance acceleration.' },
      { date: '2025',     stage: 'early', note: 'Adaptation-specific investment thesis crystallizes. First standalone adaptation funds at scale.' },
    ],

    beneficiaries: [
      'Water-management infrastructure (desalination, distributed treatment, ag-water efficiency)',
      'Heat / drought-tolerant agricultural genetics',
      'Parametric insurance / climate-risk modeling specialists',
      'Distributed cooling (heat-pump, building-envelope retrofit)',
      'Coastal-hardening / sea-wall / managed-retreat infrastructure',
      'Specialty-real-estate in climate-resilient geographies (Great Lakes, interior northwest)',
      'Indoor agriculture / vertical farming with maturing cost curves',
    ],

    trappedSectors: [
      'Coastal real-estate in 100-year-flood zones (insurance withdrawal accelerating)',
      'Florida / Gulf-coast property markets without parametric coverage',
      'Crop-monoculture exposure to climate-zone shift',
      'Tourism-dependent economies in vulnerable geographies',
    ],

    watchFor: [
      'Florida / California insurance-market parametric-product velocity',
      'Major US city water-stress event (Phoenix, Las Vegas, Atlanta water-allocation crisis)',
      'Federal climate-resilience infrastructure spending',
      'Crop-failure event triggering food-security policy response',
      'Climate-driven internal-migration data signals (Census, IRS migration data)',
    ],

    antiWatchFor: [
      'Major mitigation breakthrough (geoengineering, fusion) reducing adaptation urgency',
      'Federal-flood-insurance reauthorization without parametric structure',
      'Climate-policy reversal at federal level',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'energy-transition', direction: 'compounding',
        mechanism: 'Overlap in grid hardening and distributed-energy investment. Adaptation and mitigation share infrastructure backbone.' },
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'Climate-driven internal and international migration as compounding factor on top of fertility-decline-driven population change.' },
      { otherShiftId: 'us-china-decoupling', direction: 'conflicting',
        mechanism: 'Critical-minerals competition for adaptation-tech components. Decoupling raises adaptation infrastructure cost.' },
    ],
  },

  {
    id: 'asset-tokenization',
    slug: 'asset-tokenization',
    name: 'Asset Tokenization',
    shortName: 'Tokenization',
    category: 'active',
    era: '2024-',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: 'Bitcoin/Ethereum ETFs were institutional gate. Stablecoin legislation + RWA tokenization next. Settlement, collateral, payments rebuild.',

    thesis:
      "With the January 2024 Bitcoin spot ETF approvals (and Ethereum mid-2024), the institutionalization of crypto crossed a regulatory threshold. The 2020s next phase is: stablecoin federal legislation, real-world-asset (RWA) tokenization (Treasuries, commercial real estate, private credit, money-market funds), settlement-layer rebuild (T+0), and tokenized payments. The endgame is not 'crypto disrupts banking' but 'tokenized rails become the substrate for traditional finance' — same products, faster settlement, programmable composability. The shift is the quiet absorption of crypto-native infrastructure into mainstream finance.",

    stageHistory: [
      { date: '2009-2017', stage: 'pre-shift', note: 'Crypto as outsider asset class. Bitcoin "digital gold" narrative.' },
      { date: '2018-2020', stage: 'pre-shift', note: 'ICO bust. Institutional crypto-curious phase.' },
      { date: '2021-2022', stage: 'pre-shift', note: 'NFT mania → bear market. FTX collapse exposes governance gaps.' },
      { date: '2024 (Jan)', stage: 'early', note: 'Bitcoin spot ETF approvals (BlackRock, Fidelity, etc.). Institutional gate opens.' },
      { date: '2024-2025', stage: 'early', note: 'Stablecoin federal framework progress. Treasury tokenization (BlackRock BUIDL, etc.). RWA momentum builds.' },
    ],

    beneficiaries: [
      'Stablecoin issuers (Circle / USDC, regulated-issuer entrants)',
      'Tokenization platforms (Securitize, Ondo, Centrifuge, Maple)',
      'Settlement-layer infrastructure (Fireblocks, Anchorage, Coinbase Prime)',
      'Layer-2 / app-chain ecosystems with RWA throughput',
      'Compliant exchanges / custody (Coinbase, Anchorage, BitGo)',
      'Tradfi-crypto bridge specialists (BNY Mellon digital, JPM Coin)',
    ],

    trappedSectors: [
      'Pure speculation tokens without utility / legal structure',
      'Crypto-only firms without compliant on-ramps post-stablecoin legislation',
      'Legacy clearing-and-settlement infrastructure facing T+0 disintermediation pressure',
      'Money-market fund operators losing share to tokenized-MMF (BUIDL class)',
    ],

    watchFor: [
      'US stablecoin legislation passage',
      '$50B+ tokenized US Treasuries on-chain',
      'Major bank-issued tokenized deposit at scale',
      'T+0 securities-settlement pilot at major exchange',
      'Stablecoin-payments velocity in real economy (not just crypto-trading)',
    ],

    antiWatchFor: [
      'Major stablecoin de-peg incident',
      'SEC reversal on RWA framework',
      'Privacy-rule rollback making compliance impossible',
      'Credit cycle hitting tokenized private credit before infrastructure matures',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'ai-boom', direction: 'compounding',
        mechanism: 'AI agents transacting via stablecoins / tokenized rails — the agent economy is downstream of programmable money.' },
      { otherShiftId: 'wealth-transfer', direction: 'compounding',
        mechanism: 'Recipient generation\'s preferred asset infrastructure. The Boomer-to-Millennial wealth transfer disproportionately routes through tokenized rails.' },
      { otherShiftId: 'us-china-decoupling', direction: 'compounding',
        mechanism: 'Financial-system bifurcation. Stablecoin geopolitics — USD-denominated stablecoins as soft-power instrument vs CBDC alternatives.' },
    ],
  },

  {
    id: 'wealth-transfer',
    slug: 'wealth-transfer',
    name: 'Generational Wealth Transfer',
    shortName: 'Wealth Transfer',
    category: 'active',
    era: '2020-2050',
    stages: ['early', 'accelerating', 'peak', 'declining'],
    currentStageIndex: 0,
    stageLabel: 'Early',
    note: '$84T Boomer→Millennial transfer over 25 years. Estate, philanthropy, financial-advisory shifts. Already started; accelerating 2030+.',

    thesis:
      "The largest intergenerational wealth transfer in human history is underway: an estimated $84T moving from Boomers to Millennials and Gen X over the next 20-25 years. This is not market-prediction — it's actuarial certainty, determined by Boomer life-expectancy and asset-ownership concentration. The investment thesis: financial-advisory restructuring (fee structures, digital-native preferences), estate-planning and trust-services scale, philanthropic-vehicle proliferation, real-estate market mechanics (inherited property → renovation/sale wave), luxury-consumption recomposition, and crypto/tokenization as the recipient generation's preferred asset structure. The shift is mechanical and durable; the question is which beneficiary categories capture share.",

    stageHistory: [
      { date: '2000-2010s', stage: 'pre-shift', note: 'Wealth accumulation phase. Boomer asset peak forming.' },
      { date: '2015-2020', stage: 'pre-shift', note: 'First wave of inheritance (older Boomers passing). RIA consolidation begins.' },
      { date: '2020-2023', stage: 'early', note: 'COVID accelerated some transfers (early estate planning). Mass-affluent inheritance velocity rising.' },
      { date: '2024-2025', stage: 'early', note: 'Mid-Boomer cohort entering 70s. Transfer velocity increasing. Trust-services scale.' },
      { date: '2030+',     stage: 'early → accelerating (projected)', note: 'Peak transfer years. Annual transfer >$5T. Recipient cohort takes asset-allocation control.' },
    ],

    beneficiaries: [
      'Estate-planning law firms / trust companies',
      'RIAs serving inheritor generations (digital-native, ESG-preferring, lower-fee structures)',
      'Wealth-management technology platforms (Schwab, BlackRock, Robinhood Gold-tier)',
      'Crypto-custody infrastructure (recipient generation\'s preferred asset class)',
      'Family-office services scaling down market',
      'Renovation / new-construction in inherited-property wave',
      'Donor-advised funds and impact-investing platforms',
    ],

    trappedSectors: [
      'Traditional brokerage models with high-fee structures',
      'Insurance products poorly aligned with millennial preferences',
      'Luxury categories tied to Boomer preferences without millennial transition',
      'Mass-market retirement-planning models',
    ],

    watchFor: [
      'RIA consolidation velocity',
      'Crypto / tokenized asset adoption in estate plans',
      'Philanthropic-vehicle (DAF, foundation) growth',
      'Inherited-property rehabilitation / sale cycle in major metros',
      'Generational shift in luxury-consumption (categories)',
      'Tax-policy shifts on estate / step-up basis',
    ],

    antiWatchFor: [
      'Estate-tax reform sharply raising rates / limiting step-up basis',
      'Major financial-market drawdown reducing transferable wealth',
      'Boomer longevity surprise (people live longer, transfer slower)',
      'Catastrophic recession depleting accumulated wealth',
    ],

    relatedFindings: [],

    crossShiftInteractions: [
      { otherShiftId: 'asset-tokenization', direction: 'compounding',
        mechanism: 'Recipient generation\'s preferred asset infrastructure. Tokenization absorbs share of new-wealth deployment by default.' },
      { otherShiftId: 'demographics', direction: 'compounding',
        mechanism: 'Wealth transfer concentrates as cohorts shrink, altering inequality dynamics. The two shifts share underlying demographic dynamics.' },
      { otherShiftId: 'longevity-biology', direction: 'conflicting',
        mechanism: 'Extended Boomer longevity delays and reshapes transfer timing. The two shifts have offsetting effects on transfer velocity.' },
    ],
  },

  // ── HISTORICAL SHIFTS — the analog library (F13) ─────────────────────
  // Resolved structural shifts whose outcomes are known. Used to
  // pattern-match contemporary active shifts. The `lessons` field is the
  // analog payoff — extracted insights for application to similar shifts.

  {
    id: 'fall-of-rome-west',
    slug: 'fall-of-rome-west',
    name: 'Fall of the Roman Empire (West)',
    shortName: 'Fall of Rome',
    category: 'historical',
    era: '180-476 AD',
    stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'],
    currentStageIndex: 4,
    stageLabel: 'Resolved',
    note: 'Multi-century decline. Demographic + fiscal + institutional decay compounded. The fall was the cumulative effect, not an event.',

    thesis:
      "The Western Roman Empire's fall was a multi-century structural decline driven by the convergence of four forces: demographic stagnation (sub-replacement birth rates among Roman citizens, increased reliance on barbarian recruits), fiscal exhaustion (currency debasement of the denarius from 90% silver in 160 AD to under 5% by 270), institutional decay (the army shifted from a meritocratic professional force to a loyalty market favoring whoever paid), and barbarian integration without assimilation (foederati given land in exchange for service, but retaining tribal identity). The fall wasn't an event — it was the accumulated consequence of 200+ years of structural shifts that no single emperor or generation perceived as catastrophic. Contemporary observers consistently believed each crisis was recoverable; recovery was deferred but not achieved.",

    stageHistory: [
      { date: '180 AD',  stage: 'early',        note: 'Death of Marcus Aurelius. End of "Five Good Emperors." Commodus succession marks structural turn.' },
      { date: '235-284', stage: 'accelerating', note: 'Crisis of the Third Century. 50 emperors in 50 years. Currency debasement accelerates. Provincial breakaways.' },
      { date: '284-305', stage: 'peak',         note: 'Diocletian reforms attempt structural fix — Tetrarchy splits empire operationally. Buys time, doesn\'t reverse.' },
      { date: '376',     stage: 'declining',    note: 'Visigoth crossing of the Danube. Foederati system breaks; barbarians integrated without assimilation.' },
      { date: '410',     stage: 'declining',    note: 'Sack of Rome by Alaric\'s Visigoths. Symbolic + practical capitulation.' },
      { date: '476',     stage: 'resolved',     note: 'Odoacer deposes Romulus Augustulus. Western Empire formally ends. Eastern continues 1000 more years.' },
    ],

    beneficiaries: [
      'Visigoths, Vandals, Ostrogoths, and other foederati — territorial gains transferring intact',
      'Eastern Roman / Byzantine Empire — survived the fall by 1000+ years through structural reforms',
      'Christian church — institutional power transfer; bishops assumed governance roles in failed civil structures',
      'Frankish kingdom — filled the power vacuum in Gaul; eventually became Holy Roman Empire',
      'Local landed aristocracy outside the fragmenting urban centers — assumed quasi-state functions',
    ],

    trappedSectors: [
      'Latin senatorial / equestrian class — wealth and status both collapsed',
      'Mediterranean trade networks — long-distance commerce contracted by ~90% within two centuries',
      'Small landholders — absorbed into latifundia and proto-serfdom',
      'Roman military pensions — debasement destroyed real value; veterans became destabilizing force',
      'Urban populations dependent on annona (grain dole) — cities depopulated when supply chains collapsed',
    ],

    watchFor: [
      '(historical context — what observers tracked then)',
      'Currency purity / weight of the denarius',
      'Tax-base contraction and reliance on coercive collection',
      'Frequency of emperor turnover and military mutinies',
      'Foederati treaties and their adherence',
      'Population census trends in Italian heartland',
    ],

    antiWatchFor: [
      'Sustained economic recovery in any single province',
      'Restoration of monetary purity (never achieved at scale)',
      'Successful re-conquest of lost territories (Justinian tried; couldn\'t hold)',
    ],

    relatedFindings: [],

    crossShiftInteractions: [],

    contrarianRead: {
      consensus: 'Each crisis was framed as recoverable. Successive emperors "saved" the empire (Aurelian, Diocletian, Constantine, Theodosius). Contemporary observers believed restoration was possible.',
      lab: 'Restoration was a Lindy illusion. The decline was structural and cumulative. Each emperor deferred collapse but did not reverse it. Demographic + fiscal + institutional decay compounded faster than any single reign could repair.',
      wedge: 'Slow structural decline is invisible to contemporaries; each generation sees the prior generation as the broken one and itself as the recovery. Recognize this in any active shift where "we recovered before, we\'ll recover again" is the dominant frame.',
    },

    namedBeneficiaries: [
      { entity: 'Visigothic Kingdom', thesis: 'Acquired Iberia and southern Gaul as functional state on Roman remains; kept Roman administrative structure for 200+ years.', sizing: 'Long-tail territorial transfer.' },
      { entity: 'Byzantine Empire (Eastern Continuation)', thesis: 'Structural reforms (theme system, post-Justinian retrenchment) yielded 1000-year tail. Survival via fragmentation, not via Western reunification.', sizing: 'The "save" was selective continuation, not restoration.' },
      { entity: 'Roman Catholic Church', thesis: 'Filled the institutional vacuum. Latin literacy + governance preserved through monastic networks; civil functions transferred to bishops.', sizing: 'The cultural-institutional substitute.' },
    ],

    namedTrapped: [
      { entity: 'Italian senatorial aristocracy', thesis: 'Land-based wealth captured by foederati; patronage networks dissolved.', sizing: 'Total wealth transfer over 100 years.' },
      { entity: 'Mediterranean shipping fleets (Roman commerce)', thesis: 'Long-distance trade collapsed; pirates filled the vacuum; archaeological pottery record shows ~90% volume decline by 600 AD.', sizing: 'Network value collapsed structurally.' },
    ],

    watchMetrics: [], // historical — no live metrics

    historicalAnalogs: [],

    trackRecord: [],

    lessons: [
      'Slow structural decay is invisible to contemporaries. Each generation thinks it\'s recoverable; recovery is deferred but not achieved.',
      'Currency debasement is the canary. When fiat purchasing power compresses sustainably, structural decay is advanced.',
      'Military becoming a loyalty market (rather than meritocratic) signals end-stage institutional capture.',
      'Barbarian integration without assimilation = power transfer in slow motion. The new entrants gain administrative capacity over time even without conquest.',
      'Religious / institutional substitutes preserve cultural continuity even as political structure fails. Don\'t mistake political collapse for civilizational collapse.',
      'Eastern Empire\'s 1000-year continuation shows: fragmentation isn\'t always death. Partial collapse can yield long-tail continuity for the surviving fragment.',
    ],

    eraChart: {
      type: 'timeline',
      title: 'Denarius silver content 160-300 AD — the canary in the empire',
      subtitle: 'The Roman silver coin (denarius) was the empire\'s reserve asset. Debasement from ~95% silver to under 5% over 130 years tracks the structural decay that culminated in collapse.',
      series: [{
        id: 'silver-content',
        label: 'Silver % of denarius',
        color: 'oxblood',
        points: [
          { x: 160, y: 95 },
          { x: 200, y: 70 },
          { x: 235, y: 50 },
          { x: 250, y: 30 },
          { x: 270, y: 5 },
          { x: 300, y: 2 },
        ],
      }],
      xLabel: 'Year (AD)',
      yLabel: 'Silver content (%)',
      yMin: 0,
      yMax: 100,
    },
  },

  {
    id: 'black-death',
    slug: 'black-death',
    name: 'The Black Death',
    shortName: 'Black Death',
    category: 'historical',
    era: '1347-1351',
    stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'],
    currentStageIndex: 4,
    stageLabel: 'Resolved',
    note: '30-50% mortality across Europe in 4 years. Sudden labor scarcity → wage premium → end of feudalism arc.',

    thesis:
      "The bubonic plague pandemic that killed an estimated 30-50% of Europe's population in four years (1347-1351) triggered the most rapid demographic-economic restructuring in pre-modern history. The shock created sudden labor scarcity, which rebalanced the medieval economy in favor of labor over land — wages rose 50-100% in real terms within a generation. Attempts to legislate wages back down (the Statute of Labourers, 1351) failed. The peasant class gained mobility and bargaining power. Land values collapsed. The institutional foundations of feudalism — labor bondage, manorial obligation, serfdom — eroded over the following century. The Renaissance, the Reformation, and eventually capitalism trace partial origins to this single demographic event.",

    stageHistory: [
      { date: '1346',     stage: 'early',        note: 'Plague reaches Crimea. Genoese ships carry it westward.' },
      { date: '1347-1348', stage: 'accelerating', note: 'Mediterranean ports infected. Sicily, Marseille, Genoa hit. Death rate 30-60% in cities.' },
      { date: '1349-1350', stage: 'peak',         note: 'England, Scandinavia, Eastern Europe. ~50% population reduction in worst-hit areas.' },
      { date: '1351',     stage: 'declining',    note: 'Initial wave subsides. Recurrent outbreaks for 300 years (Plague of London 1665).' },
      { date: '1351-1450', stage: 'resolved',    note: 'Wage premium consolidates. Statute of Labourers (1351) fails. Peasant revolts (1381 England, 1358 France). Feudal labor structure collapses.' },
    ],

    beneficiaries: [
      'Surviving labor — wages doubled in real terms within 50 years',
      'Mobile / contractual workers — mobility unenforceable as estates competed for labor',
      'Yeoman farmers — accumulated land at depressed prices',
      'Cities (despite mortality) — rebuilt with wealthier survivors',
      'Banks (Medici, Fuggers) — concentrated finance as land became less productive',
      'Monastic / charitable institutions — inherited from extinct lines',
    ],

    trappedSectors: [
      'Landed aristocracy — land values collapsed, labor costs spiked',
      'Manorial system — unable to enforce serfdom against mobile labor',
      'Wool trade in pre-mortality structure — disrupted, restructured around new labor economics',
      'Religious authorities — credibility damaged by inability to explain or stop the plague',
      'Ecclesiastical landlords — bound by canon law to old wage rates while market moved',
    ],

    watchFor: [
      '(historical context)',
      'Mortality rate by region (Italy, England, Mediterranean France)',
      'Wage rates after 1351 — Statute of Labourers compliance',
      'Land sale prices and concentration patterns',
      'Recurrence of plague outbreaks',
    ],

    antiWatchFor: [
      'Successful enforcement of pre-plague wage caps (didn\'t happen)',
      'Restoration of feudal labor obligations (failed)',
    ],

    relatedFindings: [],

    crossShiftInteractions: [],

    contrarianRead: {
      consensus: 'The Black Death was a catastrophe — millions died, institutions failed, civilization regressed.',
      lab: 'It was both catastrophe AND the trigger for the structural shift to early-modern Europe. The institutional rebalancing in favor of labor created conditions for capitalism, the Reformation, and the Renaissance. Catastrophic shocks can produce durable structural improvement on the other side of the trough.',
      wedge: 'Catastrophic shocks accelerate structural change that was incipient. They don\'t cause the change; they remove institutional friction that was suppressing it. The wage rebalancing was inevitable given pre-plague labor productivity gains; the plague compressed 100 years of change into 30.',
    },

    namedBeneficiaries: [
      { entity: 'English peasantry / yeoman class', thesis: 'Acquired land at distressed prices; bargaining power on wages; eventual political voice (Peasants\' Revolt 1381 was suppressed but signaled the shift).', sizing: 'Class-level wealth transfer.' },
      { entity: 'Florentine + Northern Italian banking houses (Medici, Bardi, Peruzzi)', thesis: 'Capital concentration as land values fell; finance became more productive than land; Renaissance-era patronage emerged.', sizing: 'Concentrated wealth migration to liquid capital.' },
      { entity: 'Cities (London, Florence, Bruges, Venice)', thesis: 'Despite high mortality, rebuilt with wealthier survivors; urban-rural income gap inverted.', sizing: 'Urban concentration accelerated.' },
    ],

    namedTrapped: [
      { entity: 'Continental landed aristocracy', thesis: 'Manorial economy unworkable; many noble lines extinguished or impoverished. France\'s Hundred-Year-War nobility hit doubly hard.', sizing: 'Sustained aristocratic wealth compression.' },
      { entity: 'Catholic Church (medieval theocratic structure)', thesis: 'Inability to explain or prevent the plague damaged credibility. Set the conditions for Wycliffe (1330s-80s), then Hus, then ultimately the Reformation.', sizing: 'Long-arc institutional credibility loss.' },
    ],

    watchMetrics: [],
    historicalAnalogs: [],
    trackRecord: [],

    lessons: [
      'Sudden demographic shocks compress structural change that was already incipient. The plague didn\'t cause feudalism to end; it removed the friction that suppressed labor\'s pricing power.',
      'Catastrophic mortality creates wage premiums that rewrite economic structure. Watch labor-scarcity events for similar dynamics.',
      'Institutions whose authority depends on metaphysical claims (the medieval Church) are most fragile to events they cannot explain. Credibility damage compounds; recovery takes centuries.',
      'Land-based wealth is uniquely vulnerable to demographic collapse. Liquid / financial wealth concentrates as land becomes less productive.',
      'Cities recover faster than countryside despite higher mortality — survivors aggregate where opportunity concentrates.',
      'The Statute of Labourers shows: legislating against market forces under structural pressure fails. Wage controls cannot hold against scarcity; institutional friction collapses faster than law can repair.',
    ],

    eraChart: {
      type: 'timeline',
      title: 'English real wages 1300-1500 — the labor scarcity premium',
      subtitle: 'Real wages of English agricultural laborers indexed to 1300. The plague\'s 30-50% mortality caused wages to roughly double in real terms over 50 years. Statute of Labourers (1351) failed to suppress them.',
      series: [{
        id: 'real-wages',
        label: 'Real wage index (1300=100)',
        color: 'sage',
        points: [
          { x: 1300, y: 100 },
          { x: 1340, y: 105 },
          { x: 1351, y: 130 },
          { x: 1380, y: 175 },
          { x: 1410, y: 195 },
          { x: 1450, y: 200 },
          { x: 1500, y: 180 },
        ],
      }],
      xLabel: 'Year',
      yLabel: 'Real wage index',
      yMin: 80,
      yMax: 220,
    },
  },

  {
    id: 'industrial-revolution',
    slug: 'industrial-revolution',
    name: 'Industrial Revolution',
    shortName: 'Industrial Revolution',
    category: 'historical',
    era: '1760-1840',
    stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'],
    currentStageIndex: 4,
    stageLabel: 'Resolved (consolidated; second wave 1870+)',
    note: 'Mechanical power + steam + factory system. UK first; spread to Continent + US 1820-1870. Productivity ↑ 4-10× by 1900.',

    thesis:
      "The Industrial Revolution transformed labor productivity, settlement patterns, and political structure across Britain and subsequently the Continent and US. Three converging factors: cheap energy (coal), mechanical innovation (steam engine, spinning jenny, power loom), and capital accumulation (Atlantic trade profits, banking infrastructure). The factory system substituted concentrated mechanical labor for distributed cottage production. Real wages stagnated or declined for the first 50 years (the 'Engels Pause') before rising sharply 1840-1900. Urbanization, child labor, public health crises, and political reform (Chartism, eventual labor parties) all trace to this single transition. The investment thesis was straightforward in retrospect: own the railways, the textile mills, the iron foundries, the coal-bearing land; short the displaced cottage industries.",

    stageHistory: [
      { date: '1760-1780', stage: 'early',        note: 'Watt steam engine refinements. Spinning jenny, water frame. Factory system emerges in Lancashire.' },
      { date: '1780-1810', stage: 'accelerating', note: 'Cotton textile production scales 10×. Iron production triples. Factory towns grow rapidly.' },
      { date: '1810-1840', stage: 'peak',         note: 'Railway era begins (Stockton-Darlington 1825, Liverpool-Manchester 1830). Capital deepening across sectors.' },
      { date: '1840-1870', stage: 'declining',    note: 'First wave consolidates. UK productivity gains plateau. Continental + US catch-up phase.' },
      { date: '1870+',     stage: 'resolved',     note: 'Second Industrial Revolution begins (electricity, chemistry, steel). First wave fully integrated; second wave is distinct shift.' },
    ],

    beneficiaries: [
      'Cotton textile manufacturers (Manchester, Lancashire) — production scaled 10× in 50 years',
      'Iron and steel masters (Carron, Coalbrookdale, later Sheffield)',
      'Railway promoters and capital owners (canal interests transitioned, late entrants suffered)',
      'Coal-bearing landowners (Northumbria, Durham, South Wales)',
      'Banking and finance (Rothschild, Baring, City of London) — deployed capital into industrial expansion',
      'Atlantic shipping in cotton and grain trade',
      'Engineering specialists (Stephenson, Brunel) — created entire new professional class',
    ],

    trappedSectors: [
      'Cottage textile workers — handloom weavers\' wages collapsed 70-90% over 30 years; Luddism in protest',
      'Canal companies — disrupted by railways within a generation',
      'Wool textile traditional centers (East Anglia) — lost share to cotton-Lancashire',
      'Skilled craft guilds — undercut by factory systems',
      'Agriculture (in real terms) — relative wealth and political weight declined for 100 years',
    ],

    watchFor: [
      '(historical context)',
      'Coal output by region',
      'Steam engine deployments',
      'Railway-mile additions',
      'Textile output (cotton bales, yards woven)',
      'Iron and steel tonnage',
      'Urban population growth rates',
    ],

    antiWatchFor: [
      'Luddite or anti-machine legislation succeeding (it did not at scale)',
      'Sustained tariff protection of cottage industries (tried; failed)',
    ],

    relatedFindings: [],
    crossShiftInteractions: [],

    contrarianRead: {
      consensus: 'The Industrial Revolution was unambiguous progress — productivity, wealth, eventually higher wages.',
      lab: 'For the first 50 years (1790-1840), real wages STAGNATED or DECLINED for most workers (the Engels Pause). Productivity gains accrued to capital, not labor, until political reform forced redistribution. The "progress" framing obscures a 50-year transition crisis.',
      wedge: 'In any general-purpose-technology shift, productivity gains accrue to capital first; labor benefits arrive only after political/institutional reform forces redistribution. Time horizons matter. The "everyone benefits" frame is a 100-year average, not a 30-year experience. Watch for the political reform threshold.',
    },

    namedBeneficiaries: [
      { entity: 'Lancashire cotton mill owners (Arkwright, Peel, etc.)', thesis: 'First-mover advantage in factory mechanization; scale economics + low labor costs in early phase. Founding fortunes for 19th-century British industrial elite.', sizing: 'Industry-defining wealth transfer.' },
      { entity: 'Railway promoters (Stephenson, Hudson, Brassey)', thesis: 'Captured the second wave (1830s-50s). Land-grabs along right-of-way; capital concentration around hubs.', sizing: 'Generational wealth, though Hudson eventually went bust on speculation.' },
      { entity: 'Coal-bearing landowners (Northumberland, Durham aristocracy)', thesis: 'Mineral rights produced rentier income for 100+ years without operational involvement.', sizing: 'Aristocratic continuity through industrial transition.' },
      { entity: 'City of London (Rothschild, Baring)', thesis: 'Financed industrial expansion + railway booms; deployed capital faster than continental rivals.', sizing: 'London becomes world financial center for 100 years.' },
    ],

    namedTrapped: [
      { entity: 'Handloom weavers (1815-1850)', thesis: 'Wages collapsed from ~25 shillings/week to under 5 over 30 years. Communities destroyed; eventually absorbed into factories or emigrated.', sizing: 'Class destruction over a generation.' },
      { entity: 'Canal companies (1830s-1860s)', thesis: 'Britain\'s extensive canal network became unprofitable within a generation of railways. Some converted to recreation; many bankrupt.', sizing: 'Sector-wide write-off.' },
    ],

    watchMetrics: [],
    historicalAnalogs: [],
    trackRecord: [],

    lessons: [
      'General-purpose-technology shifts initially transfer wealth from labor to capital. Worker-side benefits arrive ~50 years after first deployment, after political reform forces redistribution.',
      'Cost-curve breakthroughs (steam, mechanization) destroy adjacent specialty trades within 1-2 generations. Handloom weavers had no transition path.',
      'Capital owners of the displaced infrastructure (canals → railways) suffer late but suddenly. Watch for substitution-risk in any cost-curve transition.',
      'Land bearing the new energy source becomes the new aristocracy. Coal-rich British nobles benefited as mineral-rights holders without operational risk.',
      'Cities concentrate economic value and political power during structural shifts. Settlement patterns shift permanently.',
      'Engineering and finance become the highest-status professions during the build-out phase. New professional classes form alongside new industries.',
    ],

    eraChart: {
      type: 'timeline',
      title: 'British coal output + cotton textile production 1750-1900',
      subtitle: 'Two indicators tracking the Industrial Revolution\'s acceleration. Coal output (energy substrate) and cotton textile output (Lancashire factory system) both inflected sharply 1780-1830.',
      series: [
        {
          id: 'coal',
          label: 'Coal output (Mt, indexed 1750=10)',
          color: 'oxblood',
          points: [
            { x: 1750, y: 10 },
            { x: 1800, y: 30 },
            { x: 1830, y: 60 },
            { x: 1860, y: 130 },
            { x: 1900, y: 280 },
          ],
        },
        {
          id: 'cotton',
          label: 'Cotton textile output (indexed 1750=10)',
          color: 'brass',
          points: [
            { x: 1750, y: 10 },
            { x: 1800, y: 80 },
            { x: 1830, y: 250 },
            { x: 1860, y: 400 },
            { x: 1900, y: 480 },
          ],
        },
      ],
      xLabel: 'Year',
      yLabel: 'Output index',
      yMin: 0,
      yMax: 500,
    },
  },

  {
    id: 'ww2-baby-boomers',
    slug: 'ww2-baby-boomers',
    name: 'WW2 + Baby Boomer Generation',
    shortName: 'WW2 + Boomers',
    category: 'historical',
    era: '1939-1970',
    stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'],
    currentStageIndex: 4,
    stageLabel: 'Resolved (long tail through 2030s)',
    note: 'Demographic + institutional + geopolitical shift. WW2 mobilized economy; Boomer cohort drove 60-year demand wave. Suburbs, consumer credit, mass college, suburbanization.',

    thesis:
      "The combination of WW2 mobilization and the post-war baby boom restructured Western economies for two generations. WW2 forced industrial capacity buildouts at peacetime-impossible scale (US output doubled 1940-1944). Post-war demobilization redirected that capacity to consumer goods, creating the most prosperous mass-consumer economy in history. The baby boom (1946-1964) produced a demographic wave that drove demand for housing, education, automobiles, retail, and eventually retirement infrastructure for 75+ years. The institutional architecture — Social Security expansion, GI Bill, FHA mortgages, suburban federal highway, public university expansion — was built around the Boomer cohort and is now structurally over-sized for the smaller cohorts that follow.",

    stageHistory: [
      { date: '1939-1945', stage: 'early',        note: 'WW2 mobilization. US industrial output doubles. Women enter workforce at scale. Federal expansion permanent.' },
      { date: '1946-1955', stage: 'accelerating', note: 'Baby boom begins. Demobilization redirected to consumer economy. Suburbanization accelerates. GI Bill creates mass higher-ed.' },
      { date: '1956-1965', stage: 'peak',         note: 'Peak boom births. Suburb construction at scale. Highway buildout. Mass-consumer culture consolidates.' },
      { date: '1965-1980', stage: 'declining',    note: 'Birth rates fall. Boomer cohort enters labor market. Inflation crisis. Vietnam + civil rights destabilize prior order.' },
      { date: '1980-2030', stage: 'resolved',     note: 'Boomers entering peak earning, then peak wealth, then retirement. Long tail driving asset prices, healthcare, real estate. Wealth-transfer phase active.' },
    ],

    beneficiaries: [
      'Suburban single-family-home builders (Levitt, Pulte) — sold to Boomer families forming households 1965-1990',
      'Big-3 automakers (GM, Ford, Chrysler) in their peak — 1955-1979, before Japanese competition',
      'Public universities (especially state systems — UC, SUNY, Big Ten) — Boomer enrollment created institutional scale',
      'Consumer-finance pioneers (consumer credit cards, mortgage finance, mutual funds — Fidelity, Dreyfus)',
      'Defense contractors (Lockheed, Boeing, Northrop) — Cold War procurement permanent',
      'Television broadcasters and consumer brands — captured first national-mass-media-formed consumer cohort',
      'Medical-pharmaceutical complex — built scale on Boomer health needs',
      'Pacific Northwest + Sunbelt real estate — population shift recipients',
    ],

    trappedSectors: [
      'Northeast/Midwest manufacturing (Pittsburgh, Detroit, Cleveland) — once Boomers shifted to Sunbelt, capital fled',
      'Pre-war urban real estate (without renaissance gentrification) — depopulated to suburbs',
      'Smokestack industries unable to adapt to globalization (1970s-90s)',
      'Public pension systems (now structurally under-funded relative to Boomer retirement)',
      'Consumer brands tied to pre-Boomer demographic — railroad passenger service, classic department stores',
    ],

    watchFor: [
      '(historical context — what observers tracked then)',
      'Birth rate and family-formation rates',
      'Suburban housing starts',
      'Automobile production and ownership rates',
      'College enrollment growth',
      'Median household income vs CPI',
      'Federal vs state spending growth',
    ],

    antiWatchFor: [
      'Sustained inflation eroding consumer-credit demand (came in 1970s, ended early-Boomer expansion)',
      'Demographic crisis interrupting demand wave (didn\'t happen pre-1965)',
    ],

    relatedFindings: [],
    crossShiftInteractions: [],

    contrarianRead: {
      consensus: 'The post-war boom was about American exceptionalism, the Marshall Plan, and superior institutions.',
      lab: 'It was 70% demographic + capital-deployment math. The "exceptional" economy was an accidentally favorable confluence of WW2 capacity buildup + the largest birth cohort in American history. Once demographics turned (Boomers passed peak earning age, Gen-X smaller, Millennial households delayed), the institutional architecture became over-sized.',
      wedge: 'Demographics drive economics on a scale political economy systematically underweights. Most "exceptional" economies are actually demographic + capital-cycle confluences. Recognize this in active shifts: the AI Boom + Wealth Transfer + Demographics interactions are doing the same kind of structural work for 2020s-2050s.',
    },

    namedBeneficiaries: [
      { entity: 'Levittown / Pulte / suburban developers', thesis: 'Built 50M+ homes for Boomer family formation 1955-1995. Developer fortunes founded.', sizing: 'Industry creation.' },
      { entity: 'University of California system', thesis: 'Master Plan (1960) created 3-tier public university system; absorbed Boomer enrollment surge; produced disproportionate share of Silicon Valley founders.', sizing: 'Institutional dynasty.' },
      { entity: 'Fidelity / Vanguard / Schwab (consumer-finance pioneers)', thesis: '401(k) creation (1978) + Boomer accumulation built $30T+ industry. Most household wealth now in these vehicles.', sizing: 'Industry-creating transfer.' },
      { entity: 'Boeing 707-737-747 era', thesis: 'Mass air travel built on Boomer-era expansion. Boeing scaled from regional to global on this single cohort\'s travel.', sizing: 'Industry definition.' },
    ],

    namedTrapped: [
      { entity: 'Detroit / Big-3 American auto', thesis: 'Couldn\'t adapt when Japanese competition + oil shock arrived 1973+. By 2008 GM and Chrysler bankrupt.', sizing: 'Industry-scale failure.' },
      { entity: 'Eastern + Pan Am (legacy airlines)', thesis: 'Couldn\'t adapt to deregulation (1978). Both bankrupt by early 1990s. Demographic-heavy hubs (NY, FL) didn\'t save them.', sizing: 'Sector exits.' },
      { entity: 'Northeast/Midwest legacy manufacturers (Bethlehem Steel, US Steel apex)', thesis: 'Lost Boomer-driven Sunbelt migration; couldn\'t compete with Japan/Korea by 1980s. Rust Belt term coined.', sizing: 'Regional economic collapse.' },
    ],

    watchMetrics: [],
    historicalAnalogs: [],
    trackRecord: [],

    lessons: [
      'Demographic waves drive economics for 60+ years on a single cohort. The Boomer arc is still resolving in 2026.',
      'War-time industrial capacity, post-war redirected, creates compounding peace-time prosperity. Capacity built for one purpose redeploys 10× faster than capacity built de novo.',
      'Institutional architecture sized for the largest cohort becomes over-sized when subsequent cohorts shrink. Pension, education, healthcare structures are now structurally mis-matched.',
      'Consumer-finance innovation (credit cards 1958, mortgages 1934, 401(k) 1978) creates compounding behavior change. Once a cohort adopts, the asset class persists past the cohort.',
      'Geographic shift follows demographic shift. Northeast → Sunbelt mirrored Boomer family-formation choices.',
      'Regional collapse trails demographic exit by 30-50 years. Detroit and Pittsburgh\'s decline started in 1965 but became visible in 1985.',
      'The wealth transfer (Boomers → Millennials) is the resolution phase of this same shift. Active for next 25 years.',
    ],

    eraChart: {
      type: 'timeline',
      title: 'US births per year 1930-1980 — the cohort wave',
      subtitle: 'The "baby boom" was a 19-year demographic surge (1946-1964). Annual US births peaked at 4.3M in 1957. The cohort that resulted has driven housing, education, consumer credit, and now retirement infrastructure for 75+ years.',
      series: [{
        id: 'births',
        label: 'Annual US births (millions)',
        color: 'brass',
        points: [
          { x: 1930, y: 2.6 },
          { x: 1936, y: 2.4 },
          { x: 1945, y: 2.9 },
          { x: 1946, y: 3.4 },
          { x: 1950, y: 3.6 },
          { x: 1957, y: 4.3 },
          { x: 1964, y: 4.0 },
          { x: 1970, y: 3.7 },
          { x: 1975, y: 3.1 },
          { x: 1980, y: 3.6 },
        ],
      }],
      xLabel: 'Year',
      yLabel: 'Births (M)',
      yMin: 2.0,
      yMax: 4.5,
    },
  },

  {
    id: 'cold-war-end',
    slug: 'cold-war-end',
    name: 'End of the Cold War',
    shortName: 'Cold War End',
    category: 'historical',
    era: '1989-1991',
    stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'],
    currentStageIndex: 4,
    stageLabel: 'Resolved (consequences ongoing)',
    note: 'Soviet collapse opened ~30y of globalization. Convergence between economies, capital flows liberated, post-1991 institutional architecture (WTO, EU expansion) built around assumed peace.',

    thesis:
      "The Soviet Union's collapse (1989-1991) ended the bipolar geopolitical order and opened a 30-year window of unipolar US dominance with deep economic convergence between Western, formerly-communist, and emerging markets. NATO expansion, EU expansion, WTO accession (China 2001), and the proliferation of cross-border supply chains all assume the post-1991 structure. Capital, goods, and ideas flowed across previously-closed borders at unprecedented velocity. The 'end of history' framing (Fukuyama 1992) captured the moment's optimism. By 2014 (Crimea), 2018 (US-China trade war), and especially 2022 (Russia-Ukraine), the assumptions of that 30-year window were unwinding.",

    stageHistory: [
      { date: '1985',     stage: 'early',        note: 'Gorbachev ascendant. Glasnost + perestroika begin internal reforms.' },
      { date: '1989',     stage: 'accelerating', note: 'Berlin Wall falls. Eastern European satellites depart Soviet bloc.' },
      { date: '1990-1991', stage: 'peak',         note: 'German reunification. Soviet coup attempt fails. Soviet Union dissolves.' },
      { date: '1992-2008', stage: 'declining',    note: 'Globalization phase. NAFTA (1994), WTO (1995), EU expansion (2004), China WTO accession (2001).' },
      { date: '2014-2022', stage: 'resolved',    note: 'Crimea (2014), US-China trade war (2018), COVID supply-chain disruption (2020), Russia-Ukraine (2022). Post-1991 assumptions visibly unwinding.' },
    ],

    beneficiaries: [
      'US multinationals — captured global expansion, especially tech (Microsoft, IBM, then Google, Amazon)',
      'China (1990-2020) — fastest economic growth in history; WTO accession was the gateway',
      'Eastern European integration economies (Poland, Czechia, Baltic states) — caught the EU expansion wave',
      'US-led financial system (USD reserve currency, SWIFT, US Treasury market)',
      'Multinational supply-chain integrators (Walmart, Costco) — globalization made them work at scale',
      'NATO expansion beneficiaries — security guarantees + investment flows',
      'Defense contractors after 9/11 — different war, but post-1991 freed capacity for it',
    ],

    trappedSectors: [
      'Russian state-owned enterprises (1990s) — collapsed; oligarchs captured the residue',
      'US heartland manufacturing — globalization redirected production to lower-cost geographies',
      'European communist parties — institutionally collapsed within a decade',
      'Cold-War-era US defense (1989-1995) — capacity over-sized; major reductions until 9/11',
      'Independent foreign-policy postures of mid-sized powers — hub-spoke structure constrained options',
    ],

    watchFor: [
      '(historical context)',
      'Soviet republics declaring independence',
      'NATO expansion timeline',
      'WTO accession milestones',
      'FDI flows to former-Soviet bloc',
      'EU integration milestones',
    ],

    antiWatchFor: [
      'Russian recovery / re-assertion (delayed but came in 2014, 2022)',
      'US-China geopolitical reversal (not anticipated; arrived 2014-2022)',
    ],

    relatedFindings: [],
    crossShiftInteractions: [],

    contrarianRead: {
      consensus: 'The Cold War ended; democracy and market economies won; globalization would be permanent.',
      lab: 'The end of the Cold War was a 30-year unipolar window, not a permanent state. The "end of history" was a transition phase that produced its own unwinding. The structural forces of the post-1991 order (NATO expansion, supply-chain integration, USD reserve dominance) generated reactive forces (Russian revisionism, Chinese assertiveness, multipolar realignment) that became visible 2014-2022.',
      wedge: 'Hegemonic moments are necessarily transitional. The structural beneficiaries of the moment generate the conditions for its reversal — by extending too far (NATO), integrating too deeply (China-WTO), or assuming too much permanence. Recognize hegemonic-extension risk in active shifts (US/China decoupling is the unwinding of this exact dynamic).',
    },

    namedBeneficiaries: [
      { entity: 'Microsoft, IBM, Cisco (1990-2000 enterprise tech)', thesis: 'Global IT spend captured by US incumbents in the unipolar window. Microsoft Office became default for the world.', sizing: 'Decade-defining enterprise dominance.' },
      { entity: 'China (state) + China-aligned multinationals (2001-2018)', thesis: 'WTO accession enabled fastest GDP growth ever recorded. ~700M lifted from poverty. Manufactured-export-led model.', sizing: 'Civilization-scale wealth gain.' },
      { entity: 'Walmart / Costco / Target (1995-2015)', thesis: 'Captured globalization-enabled supply-chain advantage. China-sourcing built the everyday-low-price thesis.', sizing: 'Retail-format definition.' },
      { entity: 'JPMorgan / Goldman Sachs (1990s deregulation + 2000s globalization)', thesis: 'Cross-border capital deployment scaled unprecedented in 20th century.', sizing: 'Banking-system reshaping.' },
    ],

    namedTrapped: [
      { entity: 'Russian state enterprise apparatus (1991-1998)', thesis: 'Collapsed; "shock therapy" privatization delivered to oligarchs. Yeltsin-era output dropped 40%+.', sizing: 'National economic destruction.' },
      { entity: 'Detroit + Rust Belt (renewed pressure 1995-2010)', thesis: 'NAFTA + China-WTO compounded earlier Japanese competition. Manufacturing employment lost ~5M jobs 1990-2010.', sizing: 'Regional economic structural shift.' },
    ],

    watchMetrics: [],
    historicalAnalogs: [],
    trackRecord: [],

    lessons: [
      'Hegemonic moments are transitional, not permanent. The structural beneficiaries generate the reaction that ends the hegemon\'s window.',
      'Institutional architecture built for the moment (NATO expansion, WTO accession terms, supply-chain integration) becomes over-extended; reversal is harder than initial build.',
      'Globalization in the 1990s-2010s was a confluence of (a) Soviet collapse + (b) Chinese WTO entry + (c) communications-and-shipping cost compression. Reversal requires reversing only one to start the unwinding.',
      'Reserve-currency status compounds during hegemonic windows; it doesn\'t guarantee permanence. Watch reserve-currency challenges as leading indicators of structural unwind.',
      '"End of history" framings systematically underweight reversal risk. The dominant ideology of a hegemonic moment is the most-mispriced asset.',
      'Reactive forces (Russian revisionism, Chinese assertiveness) take 20-30 years to become visible — they accumulate as state capacity rebuilds, not as immediate response.',
    ],

    eraChart: {
      type: 'timeline',
      title: 'NATO members 1949-2024 + global trade as % GDP',
      subtitle: 'Two indicators tracking the post-1991 unipolar order. NATO expanded 16 → 32 members. Global trade share of GDP roughly doubled before plateauing post-2008 and then declining.',
      series: [
        {
          id: 'nato',
          label: 'NATO member count',
          color: 'brass',
          points: [
            { x: 1949, y: 12 },
            { x: 1989, y: 16 },
            { x: 1999, y: 19 },
            { x: 2004, y: 26 },
            { x: 2017, y: 29 },
            { x: 2024, y: 32 },
          ],
        },
        {
          id: 'trade',
          label: 'Global trade % GDP (×0.7 to fit scale)',
          color: 'sage',
          points: [
            { x: 1990, y: 28 },
            { x: 2000, y: 35 },
            { x: 2008, y: 41 },
            { x: 2020, y: 38 },
            { x: 2024, y: 36 },
          ],
        },
      ],
      xLabel: 'Year',
      yLabel: 'Count / %',
      yMin: 10,
      yMax: 45,
    },
  },

  {
    id: 'internet-boom',
    slug: 'internet-boom',
    name: 'Internet Boom (Dot-Com Era)',
    shortName: 'Internet Boom',
    category: 'historical',
    era: '1995-2001',
    stages: ['early', 'accelerating', 'peak', 'declining', 'resolved'],
    currentStageIndex: 4,
    stageLabel: 'Resolved (corrective bust 2000-2002; recovery 2004+)',
    note: 'Browser → mass adoption → over-investment → bust. Consumer Internet built on telecom over-capacity. NASDAQ peak Mar-2000; ~78% drawdown by 2002. Survivors became 2010s incumbents.',

    thesis:
      "The Internet Boom (1995-2001) was the first general-purpose-technology cycle of the digital era. Mosaic browser (1993) → Netscape IPO (1995) → mass adoption → over-investment → bust (2000-2002) → consolidation → infrastructure-survivor compounding. Three structurally important takeaways: (1) The bust eliminated ~$5T market cap but the technology continued advancing. (2) Picks-and-shovels (Cisco, Oracle, hardware) suffered hardest in the bust because they had been levered to build-out demand that paused; pure consumer plays (eBay, Amazon, Yahoo) had different bust dynamics. (3) The eventual winners (Google IPO 2004, Facebook 2004, Amazon survival) emerged FROM the wreckage — late-1990s incumbents (Yahoo, AOL, Excite, Lycos) were displaced. The investment lesson: cost-curve breakthroughs produce 10-year boom-bust-recovery cycles; survivor-bias recovery delivers most of the durable returns.",

    stageHistory: [
      { date: '1993-1994', stage: 'early',        note: 'Mosaic browser, Netscape Navigator. Consumer access through dial-up ISPs (AOL, Compuserve).' },
      { date: '1995-1998', stage: 'accelerating', note: 'Netscape IPO (Aug 1995), Yahoo (1996), Amazon (1997). Telecom over-build (Worldcom, Global Crossing).' },
      { date: '1998-2000', stage: 'peak',         note: 'NASDAQ rises 400%. "Click and mortar." Pets.com / WebVan / Kozmo. Greenspan "irrational exuberance" speech (1996) ignored.' },
      { date: '2000-2002', stage: 'declining',    note: 'NASDAQ peaks Mar-2000 at 5048; bottoms Oct-2002 at 1108 (-78%). Worldcom collapse (2002). Capacity write-offs.' },
      { date: '2002-2010', stage: 'resolved',     note: 'Survivors compound. Google IPO (2004), Facebook (2004), Apple iPhone (2007), Amazon Web Services (2006). Mobile-internet wave is the second leg.' },
    ],

    beneficiaries: [
      'Survivor consumer giants — Amazon, eBay, Google, Apple — captured durable share once incumbents fell',
      'Telecom infrastructure long-term — fiber over-build of late-90s became cheap bandwidth in 2000s, enabled YouTube, Netflix',
      'Cisco (briefly) — networking gear demand peaked 1999-2000',
      'Bandwidth + colocation late-stage (Equinix, Digital Realty) — survived bust to dominate cloud era',
      'Specific late-stage entrants (Salesforce 1999) — born during peak but built durable B2B',
      'Pacific Northwest + Bay Area real estate — captured engineering migration',
    ],

    trappedSectors: [
      'Pets.com, WebVan, Kozmo, eToys, Boo.com, Pets.com — consumer-Internet over-builds with no path to profitability',
      'Telecom equipment (Lucent, Nortel, Worldcom) — debt-funded over-build collapsed catastrophically',
      'AOL — peak 2000, declined; merged with Time Warner (Jan 2000) at the absolute top',
      'Yahoo — peaked 2000, displaced by Google in search; never recovered',
      'Sun Microsystems — high-end server margins evaporated in commoditization',
      'Pure-content portals — Lycos, Excite, AltaVista, GeoCities — all displaced or absorbed',
    ],

    watchFor: [
      '(historical context)',
      'NASDAQ valuation (peaked at 5048 Mar-2000)',
      'Internet penetration rates',
      'IPO velocity and post-IPO returns',
      'Ad-spending shifts to online',
      'Broadband subscriber growth',
    ],

    antiWatchFor: [
      '(retrospectively visible)',
      'Capex outpacing revenue at telecoms',
      'IPO-to-profit time stretching beyond 5 years',
      'Cash-burn rates accelerating without revenue ramp',
      'Consumer-internet sectors built on subsidized economics',
    ],

    relatedFindings: [],
    crossShiftInteractions: [],

    contrarianRead: {
      consensus: '(during the boom) This is a new economy. Old valuation rules don\'t apply. The Internet changes everything.',
      lab: 'It was a cost-curve breakthrough creating real durable value, but valuations decoupled from earnings power. The bust was inevitable; the recovery was inevitable; the survivors compounded for 25 years. Both extremes (everything is broken / everything is fine) were wrong.',
      wedge: 'In any cost-curve-breakthrough cycle, expect: (1) over-build phase 3-5 years, (2) bust phase 1-2 years with 70-90% drawdowns, (3) survivor-bias compounding 10-20 years. The wealth-creating phase is BOTH the boom and the recovery. The bust eliminates pretenders but not the underlying technology.',
    },

    namedBeneficiaries: [
      { entity: 'Amazon (1997 IPO)', thesis: 'Survived bust by burning capital but staying lean enough; AWS (2006) became second business; today $2T+ market cap.', sizing: 'Cycle-defining survivor compounding.' },
      { entity: 'Google (2004 IPO)', thesis: 'Late-entrant in search; founded 1998 but IPO\'d post-bust; captured incumbent (Yahoo) displacement; PageRank moat held for 15+ years.', sizing: 'Industry-defining late-entrant.' },
      { entity: 'Apple (post-2003)', thesis: 'iPod (2001) → iPhone (2007) was the mobile-internet wave. Capitalized on infrastructure built but unused during bust. Largest market-cap company by 2018.', sizing: 'Cycle-leveraging late-stage compounding.' },
      { entity: 'Equinix / Digital Realty / colocation', thesis: 'Acquired distressed datacenters during bust at <30% replacement cost. Served the cloud era 2010+.', sizing: 'Distressed-asset roll-up.' },
    ],

    namedTrapped: [
      { entity: 'AOL Time Warner merger (Jan-2000)', thesis: 'Largest-ever merger announced at absolute market top. Combined market cap: $350B. By 2002: <$60B. Eventually unwound at fraction of value.', sizing: 'Merger-of-the-decade failure.' },
      { entity: 'Worldcom (2002 bankruptcy)', thesis: 'Debt-funded telecom over-build + accounting fraud. $107B accounting restatement; Bernie Ebbers eventually convicted.', sizing: 'Largest US bankruptcy at the time.' },
      { entity: 'Pets.com / Kozmo / WebVan (consumer-internet 1999-2001)', thesis: 'IPO\'d in 1999, all bankrupt by 2001. Burned ~$1B+ each on subsidized economics.', sizing: 'Cohort-wide failure.' },
    ],

    watchMetrics: [],
    historicalAnalogs: [],
    trackRecord: [],

    lessons: [
      'Cost-curve breakthroughs produce boom-bust-recovery cycles of ~10-15 years. The wealth creation comes from survival into the recovery, not from peak-cycle exposure.',
      'Picks-and-shovels (telecom, hardware, infrastructure) suffer worst in the bust because they were levered to build-out demand that pauses. Application-layer with revenue may have softer landings.',
      'Late entrants (Google 1998, Facebook 2004) often beat incumbents (Yahoo, AOL, Friendster) because they built post-bust without the cost-structure baggage.',
      'Mergers at peak are leading indicators of correction. AOL+Time Warner is the canonical example.',
      'Pure consumer-internet plays without revenue model fail catastrophically; B2B SaaS and infrastructure have softer trajectories. Watch revenue model maturity.',
      'Real-estate exposure to engineering hubs benefits durably, even through bust. Bay Area real estate compounded through 2000-2002 contraction.',
      'Surviving 70-90% drawdowns is a feature of the cycle, not a defect. The "this time it\'s different" frame at peak is wrong; the "this time it\'s broken" frame at trough is also wrong.',
    ],

    eraChart: {
      type: 'timeline',
      title: 'NASDAQ Composite 1995-2010 — boom, bust, recovery arc',
      subtitle: 'NASDAQ peaked at 5048 (Mar 2000), bottomed at 1108 (Oct 2002 — a 78% drawdown), and didn\'t recover to its 2000 peak until 2015. The infrastructure build was real; the valuations were not. Survivors compounded for 25 years.',
      series: [{
        id: 'nasdaq',
        label: 'NASDAQ Composite',
        color: 'oxblood',
        points: [
          { x: 1995, y: 1000 },
          { x: 1997, y: 1570 },
          { x: 1999, y: 4070 },
          { x: 2000, y: 5048 },
          { x: 2001, y: 1950 },
          { x: 2002, y: 1108 },
          { x: 2004, y: 2175 },
          { x: 2007, y: 2650 },
          { x: 2010, y: 2650 },
        ],
      }],
      xLabel: 'Year',
      yLabel: 'NASDAQ',
      yMin: 800,
      yMax: 5200,
    },
  },

];

// ════════════════════════════════════════════════════════════════════
// TECTONIC_DEPTH — depth-field placeholders + historical-analog links
// for the 12 ACTIVE shifts (F13).
//
// The active shifts kept their core data inline above. Their depth
// fields live HERE so they're easy to find, easy to edit, and the
// "what needs author input" surface is one location, not 12.
//
// Schema per entry:
//   contrarianRead: { consensus, lab, wedge } | null
//   namedBeneficiaries: [{ entity, ticker?, thesis, risk?, sizing }]
//   namedTrapped: [{ entity, ticker?, thesis, sizing }]
//   watchMetrics: [{ metric, threshold, currentValue?, asOf?, interpretation }]
//   historicalAnalogs: [{ shiftId, similarity (0-1), keyDifference }]
//   trackRecord: [{ date, call, outcome, notes }]
//
// Authoring discipline: contrarianRead and watchMetrics current values
// are the differentiators that make this paid-tier content. Empty
// arrays are honest placeholders — they signal "not yet authored."
// ════════════════════════════════════════════════════════════════════

export const TECTONIC_DEPTH = {
  'ai-boom': {
    contrarianRead: null, // TODO: author { consensus, lab, wedge }
    namedBeneficiaries: [], // TODO: specific tickers + sizing
    namedTrapped: [],
    watchMetrics: [], // TODO: quantitative thresholds with current values
    historicalAnalogs: [
      { shiftId: 'internet-boom', similarity: 0.7,
        keyDifference: 'AI is on an AGI trajectory; Internet was application-layer commoditization. Foundation-model labs may not bust the same way pure-internet plays did. Watch for capacity over-build vs revenue-ramp gap (the Internet-boom signature).' },
      { shiftId: 'industrial-revolution', similarity: 0.6,
        keyDifference: 'AI substitutes cognitive + physical labor; Industrial Revolution was mechanical only. Distribution of gains may follow the same pattern (capital first, labor later) but on a faster timeline given communications speed.' },
    ],
    trackRecord: [],
  },

  'energy-transition': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'industrial-revolution', similarity: 0.6,
        keyDifference: 'Energy Transition is REPLACEMENT of incumbent infrastructure; Industrial Revolution was greenfield. Different transition friction; legacy incumbents have political weight that didn\'t exist in 1760s Britain.' },
    ],
    trackRecord: [],
  },

  'us-china-decoupling': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'cold-war-end', similarity: 0.7,
        keyDifference: 'Cold War End converged previously-bifurcated economies; this REVERSES that. Same structural forces running backwards. Watch the unwinding sequence — it tracks 1989-2008 in reverse order (financial integration first, then trade, then alliances).' },
    ],
    trackRecord: [],
  },

  'demographics': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'black-death', similarity: 0.4,
        keyDifference: 'Black Death was sudden mortality (3-5 years, 30-50% loss); demographic decline is gradual fertility decline (50+ years, 10-30% loss). Both create labor scarcity but on different timescales. The wage-premium effect arrives slowly here.' },
      { shiftId: 'fall-of-rome-west', similarity: 0.5,
        keyDifference: 'Roman demographic decline + institutional decay compounded over 200 years. Modern decline is faster but with stronger institutional substitutes (immigration, automation, AI). The "structural-cultural substrate is the lever" finding applies to both.' },
    ],
    trackRecord: [
      { date: '2026-05-07', call: 'Pre-registered: no OECD sub-replacement country produces cohort recovery to TFR 1.7 via primarily-policy means by 2035.', outcome: 'pending', notes: 'See Lab finding popdec/2026/positive-case-search/v1. Resolution 2030-2035.' },
    ],
  },

  'humanoid-robotics': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'industrial-revolution', similarity: 0.6,
        keyDifference: 'Industrial Revolution mechanized PHYSICAL labor in STRUCTURED environments. Humanoid robotics extends to physical labor in UNSTRUCTURED environments and adds AI-driven adaptability. Substitution scope is broader but cost curve is on faster trajectory.' },
    ],
    trackRecord: [],
  },

  'space-economy': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'industrial-revolution', similarity: 0.5,
        keyDifference: 'Both are cost-curve breakthroughs (steam → mechanical labor; reusable launch → orbital access). Space has higher capital intensity + longer cycle times; economic structure resembles 1840s railroads more than 1880s factories.' },
      { shiftId: 'internet-boom', similarity: 0.5,
        keyDifference: 'Both have application-layer winners after infrastructure build-out. Space is 20+ years behind Internet in this arc; current SpaceX dominance is comparable to Cisco/Oracle/Sun pre-2000, not Google.' },
    ],
    trackRecord: [],
  },

  'crispr-gene-editing': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'internet-boom', similarity: 0.4,
        keyDifference: 'CRISPR is a general-purpose biological tool; Internet was a general-purpose information tool. Adoption arc 10x longer because biology is regulated. Boom-bust dynamics may compress less aggressively.' },
    ],
    trackRecord: [],
  },

  'longevity-biology': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'industrial-revolution', similarity: 0.3,
        keyDifference: 'Modern medicine emerged 1850-1900 as part of broader industrial-era productivity. Longevity biology is comparable in scope but inverts the model — DELAY aging rather than CURE disease. Different regulatory paradigm; different consumer expectation.' },
    ],
    trackRecord: [],
  },

  'defense-industrial-base': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'ww2-baby-boomers', similarity: 0.7,
        keyDifference: 'WW2 was wartime emergency mobilization (5-year compression of decade-scale buildouts). Current is peacetime structural reconstitution against a 30-year decay. Slower but more durable; less cyclical risk on the back end.' },
    ],
    trackRecord: [],
  },

  'climate-adaptation': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'black-death', similarity: 0.4,
        keyDifference: 'Black Death was 5-year discrete shock; climate adaptation is multi-decade structural shift with discrete shock-events embedded (heat domes, water crises, hurricane cycles). Insurance mechanisms didn\'t exist pre-modern; they\'re central now and create different signal patterns.' },
    ],
    trackRecord: [],
  },

  'asset-tokenization': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'cold-war-end', similarity: 0.4,
        keyDifference: 'Cold War End built up Western financial infrastructure (USD reserve dominance, SWIFT, Fed-led system). Tokenization rebuilds the SETTLEMENT LAYER of that same infrastructure — same plumbing, faster pipes. Watch for hegemon-extension risk (over-reliance on USD-stablecoin geopolitics).' },
      { shiftId: 'internet-boom', similarity: 0.5,
        keyDifference: 'Both are infrastructure-rebuild cycles for an established function (commerce → tokenized settlement). Internet boom\'s late entrants (Amazon AWS, Salesforce) won the durable franchises; expect similar in tokenization — current incumbents (Coinbase, Circle) may not be the long-term winners.' },
    ],
    trackRecord: [],
  },

  'wealth-transfer': {
    contrarianRead: null,
    namedBeneficiaries: [],
    namedTrapped: [],
    watchMetrics: [],
    historicalAnalogs: [
      { shiftId: 'ww2-baby-boomers', similarity: 0.8,
        keyDifference: 'This shift IS the resolution phase of WW2-Boomers. Same cohort, different stage. The accumulated wealth of the post-war boom is transferring; institutional architecture (RIAs, trust services, philanthropic vehicles) is mid-transformation.' },
    ],
    trackRecord: [],
  },
};

/** Merge core shift data with depth fields. */
export function getTectonicShift(slug) {
  const base = TECTONIC_SHIFTS.find((s) => s.slug === slug);
  if (!base) return null;
  // Historical shifts have inline depth; active shifts pull from TECTONIC_DEPTH
  const depth = TECTONIC_DEPTH[slug] || {};
  return {
    ...base,
    contrarianRead: base.contrarianRead ?? depth.contrarianRead ?? null,
    namedBeneficiaries: base.namedBeneficiaries ?? depth.namedBeneficiaries ?? [],
    namedTrapped: base.namedTrapped ?? depth.namedTrapped ?? [],
    watchMetrics: base.watchMetrics ?? depth.watchMetrics ?? [],
    historicalAnalogs: base.historicalAnalogs?.length ? base.historicalAnalogs : (depth.historicalAnalogs ?? []),
    trackRecord: base.trackRecord?.length ? base.trackRecord : (depth.trackRecord ?? []),
    lessons: base.lessons ?? [],
  };
}

export function listTectonicShifts() {
  return TECTONIC_SHIFTS.map((s) => getTectonicShift(s.slug));
}

export function listByCategory(category) {
  return listTectonicShifts().filter((s) => s.category === category);
}

/** Resolve a cross-shift link to the linked shift's name + slug. */
export function resolveShiftRef(otherShiftId) {
  const target = TECTONIC_SHIFTS.find((s) => s.id === otherShiftId);
  if (!target) return null;
  return { name: target.name, slug: target.slug, category: target.category };
}
